import { useState, useEffect, useCallback } from "react";
import { todayLocal } from "./utils/dateLocal";
import "@/App.css";
import axios from "axios";

// Composants extraits
import JurisConfirmModal from "./components/JurisConfirmModal";
import MessagePopup from "./components/MessagePopup";
import EtablissementModal from "./components/EtablissementModal";
import RefusPrecedentModal from "./components/RefusPrecedentModal";
import PDFPreviewModal from "./components/PDFPreviewModal";
import { openReport as openReportTab } from "./lib/reportViewerBus";
import DajPrintModal from "./components/DajPrintModal";
import PDFCalibrationTool from "./components/PDFCalibrationTool";
import AttestationPDFCalibrationTool from "./components/AttestationPDFCalibrationTool";
import GestionRapportsModal from "./components/GestionRapportsModal";
import { WinButton, WinInput, WinSectionHeader } from "./components/WinComponents";
import DateInput from "./components/DateInput";
import {
  FilePlus, Search, Save, SaveAll, Printer, Calculator, ClipboardList,
  UserCircle2, Users, FileCheck, XCircle, Stethoscope, Scale,
  ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, UploadCloud, Loader2
} from "lucide-react";
import { formatTelephone } from "./utils/formatters";
import RequerantTab from "./components/RequerantTab";
import ConjointTab from "./components/ConjointTab";
import LiensTab from "./components/LiensTab";
import RevenusTab from "./components/RevenusTab";
import ActifsTab from "./components/ActifsTab";
import DecisionTab from "./components/DecisionTab";
import StatutTab from "./components/StatutTab";
import ServiceTab from "./components/ServiceTab";
import CalculatriceModal from "./components/CalculatriceModal";
import JurisEvolutionConfigModal from "./components/JurisEvolutionConfigModal";
import NouveauModal from "./components/NouveauModal";
import QuestionnaireModal from "./components/QuestionnaireModal";
import ModeDemandeModal from "./components/ModeDemandeModal";
import SearchModal from "./components/SearchModal";
import FIRModal from "./components/FIRModal";
import PartieAdverseModal from "./components/PartieAdverseModal";
import AttestationModal from "./components/AttestationModal";
import RefusModal from "./components/RefusModal";
import HomologModal from "./components/HomologModal";
import ExpertiseModal from "./components/ExpertiseModal";
import RechercheModal from "./components/RechercheModal";
import JumelageModal from "./components/JumelageModal";
import FermetureLotModal from "./components/FermetureLotModal";
import ReactivationModal from "./components/ReactivationModal";
import QuestionnaireLectureModal from "./components/QuestionnaireLectureModal";
import TransfertModal from "./components/TransfertModal";
import ProvReciproModal from "./components/ProvReciproModal";
import NoteRevisionModal from "./components/NoteRevisionModal";
import AvocatsModal from "./components/AvocatsModal";
import InteretsOpposesResultsModal from "./components/InteretsOpposesResultsModal";
import ComparutionModal from "./components/ComparutionModal";
import GestionUtilisateursModal from "./components/GestionUtilisateursModal";
import GestionCategoriesQVerifModal from "./components/GestionCategoriesQVerifModal";
import GestionBureauxEloignesModal from "./components/GestionBureauxEloignesModal";
import GestionQuestionnaireEntrevueModal from "./components/GestionQuestionnaireEntrevueModal";
import ChangerBureauModal from "./components/ChangerBureauModal";
import { formatNomPropre, formatNAS, validateNAS } from "./utils/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Date de version (dernière mise à jour du code de l'application)
// À mettre à jour à chaque release significative
const APP_VERSION_DATE = "2026-02-15";

// ============================================
// APPLICATION PRINCIPALE
// ============================================

// Détermine si un type_lien correspond au conjoint (case-insensitive, tolérant au padding CHAR(N))
const isConjointType = (t) => (t || '').trim().toLowerCase() === 'conjoint';

// Détermine si un type_lien correspond à un enfant (case-insensitive, tolérant au padding CHAR(N))
const isEnfantType = (t) => (t || '').trim().toLowerCase() === 'enfant';

function App({ authUser, onLogout }) {
  const defaultRegion = ((authUser?.region || '').trim()) || '90';
  // Bureau courant de la session : démarre toujours sur le bureau par défaut du profil.
  const [currentBureau, setCurrentBureau] = useState(((authUser?.bureau || '').trim()) || '01');
  const defaultBureau = currentBureau;
  const isAdmin = authUser?.is_admin ?? (authUser?.role === 'admin');
  const canSupprimer = authUser?.can_supprimer ?? isAdmin;
  const canJumelage = authUser?.can_jumelage ?? isAdmin;
  // Etats
  const [currentTab, setCurrentTab] = useState('requerant');
  const [currentDossier, setCurrentDossier] = useState(null);
  const [currentClient, setCurrentClient] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [stats, setStats] = useState({ total_clients: 0, total_daj: 0 });
  const [message, setMessage] = useState('');
  const [showCalculatrice, setShowCalculatrice] = useState(false);
  const [jeSyncing, setJeSyncing] = useState(false);
  const [jeSyncPhase, setJeSyncPhase] = useState('');  // 'validation' | 'sync'
  const [showJEConfig, setShowJEConfig] = useState(false);
  const [showFir, setShowFir] = useState(false);
  const [firInitialId, setFirInitialId] = useState(null);
  const [showRecherche, setShowRecherche] = useState(false);
  const [showAvocats, setShowAvocats] = useState(false);
  const [showCalibration, setShowCalibration] = useState(false);
  const [showAttestCalibration, setShowAttestCalibration] = useState(false);
  const [showGestionRapports, setShowGestionRapports] = useState(false);
  const [showFormulairesMenu, setShowFormulairesMenu] = useState(false);
  const [formulairesRapports, setFormulairesRapports] = useState([]);
  // Choix de langue pour le formulaire « Réciprocité » (menu Formulaires).
  const [reciprociteChoice, setReciprociteChoice] = useState(null);
  const [impressionCtx, setImpressionCtx] = useState(null); // contexte impression DAJ courante
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showImpressionSub, setShowImpressionSub] = useState(false);
  const [showGestionUtilisateurs, setShowGestionUtilisateurs] = useState(false);
  const [showGestionCatQVerif, setShowGestionCatQVerif] = useState(false);
  const [showGestionQEntrevue, setShowGestionQEntrevue] = useState(false);
  const [showBureauxEloignes, setShowBureauxEloignes] = useState(false);
  const [bureauxEloignes, setBureauxEloignes] = useState(new Set());
  const [showQuestionnaireLecture, setShowQuestionnaireLecture] = useState(false);
  const [clientSelectionne, setClientSelectionne] = useState(false);
  const [showTransfertModal, setShowTransfertModal] = useState(false);
  const [showProvRecipro, setShowProvRecipro] = useState(false);
  const [showNoteRevision, setShowNoteRevision] = useState(false);
  const [showOutilsMenu, setShowOutilsMenu] = useState(false);
  const [showJumelage, setShowJumelage] = useState(false);
  const [showStatutMenu, setShowStatutMenu] = useState(false);
  const [showFermetureLot, setShowFermetureLot] = useState(false);
  const [showReactivation, setShowReactivation] = useState(false);
  const [pendingNouveau, setPendingNouveau] = useState(null);
  const [showChangerBureau, setShowChangerBureau] = useState(false);
  const [confirmBureauSwitch, setConfirmBureauSwitch] = useState(false);
  const [showAdverse, setShowAdverse] = useState(false);
  const [showAttestation, setShowAttestation] = useState(false);
  const [showRefus, setShowRefus] = useState(false);
  const [showComparution, setShowComparution] = useState(false);
  const [showDemandeMenu, setShowDemandeMenu] = useState(false);
  const [showHomolog, setShowHomolog] = useState(false);
  const [showExpertise, setShowExpertise] = useState(false);
  const [nasError, setNasError] = useState('');
  const [showEtablissement, setShowEtablissement] = useState(false);
  const [showRefusPrecedent, setShowRefusPrecedent] = useState(false);
  const [refusPrecedentData, setRefusPrecedentData] = useState(null);  const [etablissementSelectionne, setEtablissementSelectionne] = useState(null);
  // Date de dernière modification du dossier ouvert (provient de addadatestatut)
  const [dossierDateModif, setDossierDateModif] = useState(null);
  // Date+heure de la dernière mise à jour du backend (lu depuis /api/app-version au mount)
  const [appVersionDt, setAppVersionDt] = useState(APP_VERSION_DATE);
  useEffect(() => {
    // Récupération robuste avec retry pour gérer les 404 transitoires
    // pendant les rechargements WatchFiles (uvicorn --reload).
    let cancelled = false;
    const fetchVersion = async (attempt = 0) => {
      try {
        const res = await axios.get(`${API}/app-version`, { timeout: 4000 });
        if (!cancelled && res.data?.version_datetime) {
          setAppVersionDt(res.data.version_datetime);
        }
      } catch (e) {
        if (cancelled) return;
        // Retry jusqu'à 3 fois avec backoff (3s, 6s, 12s) pour couvrir un reload backend
        if (attempt < 3) {
          setTimeout(() => fetchVersion(attempt + 1), 3000 * Math.pow(2, attempt));
        } else {
          console.warn('[app-version] /api/app-version indisponible après plusieurs tentatives');
        }
      }
    };
    fetchVersion();
    // Refresh à chaque retour de focus de la fenêtre (utile après un git pull + reload backend)
    const onFocus = () => fetchVersion();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; window.removeEventListener('focus', onFocus); };
  }, []);
  
  // Popup de messages
  const [popup, setPopup] = useState({ isOpen: false, title: '', message: '', type: 'info', details: '' });
  const [jurisConfirm, setJurisConfirm] = useState({ open: false, noSeq: '', matterId: null, fields: [] });
  const [confirmDirty, setConfirmDirty] = useState({ isOpen: false, action: null });
  // PARITÉ LEGACY (ValidInfoFin "Mineur") : lorsqu'un requérant est mineur à la
  // date de la demande, les onglets Revenus/Actifs sont désactivés et les montants
  // financiers effacés. `revenuActifDisabled` pilote la désactivation des onglets ;
  // `confirmMineur` pilote l'avertissement Oui/Non déclenché au Leave de la date de
  // naissance.
  const [revenuActifDisabled, setRevenuActifDisabled] = useState(false);
  const [confirmMineur, setConfirmMineur] = useState({ isOpen: false });
  
  // Modal "Nouveau"
  const [showNouveauModal, setShowNouveauModal] = useState(false);
  const [showModeDemande, setShowModeDemande] = useState(false);
  const [modeDemande, setModeDemande] = useState(null); // 'reguliere' | 'conditionnelle' | 'entrevue'
  const [sansEntrevue, setSansEntrevue] = useState(false);
  const [nouveauForm, setNouveauForm] = useState({ nom: '', prenom: '', date_naissance: '', nas: '' });

  // Modal "Nouvelle image" — parité legacy CodeDajNew.vb:295-356.
  // Affichée quand l'utilisateur modifie un champ critique d'un dossier
  // existant. Capture la note de révision (adda680).
  const [revisionModal, setRevisionModal] = useState({
    open: false,
    changements: [],
    image_actuelle: '',
    image_nouvelle: '',
    note: '',
    pendingPayload: null,
  });
  const [duplicateClients, setDuplicateClients] = useState([]);
  const [showDuplicateList, setShowDuplicateList] = useState(false);
  const [pendingPriorFir, setPendingPriorFir] = useState(null);
  const [isSearchingDuplicates, setIsSearchingDuplicates] = useState(false);
  
  // Gestion des liens familiaux
  const [showGestionLiens, setShowGestionLiens] = useState(false);
  const [liensListe, setLiensListe] = useState([]);
  const [lienEnEdition, setLienEnEdition] = useState(null);
  const [isLoadingLiens, setIsLoadingLiens] = useState(false);
  
  // Mode d'edition : 'vide' | 'nouveau' | 'edition'
  const [editMode, setEditMode] = useState('vide');
  const [isDirty, setIsDirty] = useState(false);
  
  // Aperçu PDF
  const [pdfPreview, setPdfPreview] = useState({ open: false, previewUrl: null, downloadUrl: null, seq: null });
  const [dajPrint, setDajPrint] = useState({ open: false, seq: null, conjointAvailable: false });

  // === Info manquante — indicateur visuel de cellule ==============
  // Liste maintenue par StatutTab (via callback onMissingItemsChange) +
  // fetch autonome (voir useEffects plus bas, après déclaration de
  // formData) pour que les indicateurs de cellule soient présents dès
  // l'ouverture d'un onglet, sans passer d'abord par Statut.
  const [missingItems, setMissingItems] = useState([]);
  // Statut calculé du dossier (label FR + code 0..4 + addastatut) —
  // affiché dans l'en-tête à droite de "Date Demande" avec le même
  // format que la StatutTab : `addastatut / chemine`.
  const [dossierStatut, setDossierStatut] = useState({ label: '', code: 0, addastatut: '' });


  
  // Désactiver l'auto-remplissage du navigateur sur tous les champs
  useEffect(() => {
    const disableAutocomplete = () => {
      document.querySelectorAll('input, select, textarea').forEach(el => {
        if (!el.getAttribute('autoComplete')) {
          el.setAttribute('autoComplete', 'one-time-code');
        }
      });
    };
    disableAutocomplete();
    const observer = new MutationObserver(disableAutocomplete);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  
  const showPopup = (message, type = 'info', title = '', details = '') => {
    setPopup({ isOpen: true, title, message, type, details });
  };

  /**
   * Ouvre la modale Attestation.
   *
   * PARITÉ LEGACY (CodeAttest.vb) : le bouton Attestation ouvre TOUJOURS
   * `frmAttest`. Les 3 onglets restent accessibles :
   *   • "Liste" — voir, imprimer, supprimer les attestations existantes
   *   • "Attestation" — détails de l'attestation sélectionnée dans Liste
   *   • "Demande d'aide juridique" — créer une nouvelle attestation
   *
   * `LoadNouvAttest` (l'éligibilité) est uniquement utilisée pour peupler la
   * grille "DAJ disponibles" de l'onglet Demande. Si aucun DAJ n'est éligible,
   * la grille reste vide et le bouton Enregistrer est désactivé — mais le
   * formulaire s'ouvre quand même pour consulter/imprimer les attestations
   * précédentes.
   *
   * Bug corrigé (v31) : la version précédente bloquait toute ouverture du
   * modal quand le DAJ courant n'était pas éligible, ce qui empêchait
   * l'accès aux attestations existantes du client.
   */
  const handleOuvrirAttestation = async () => {
    const pkDaj = currentDossier?.pk_daj || currentDossier?.no_seq_daj || formData.no_sequence;
    const noclientCourant = formData.noclient || currentDossier?.noclient;
    if (!pkDaj || !noclientCourant) {
      showPopup(
        "Aucun dossier n'est ouvert. Ouvrez un dossier avant d'accéder aux attestations.",
        "warning",
        "Attestation"
      );
      return;
    }
    // Parité legacy : bloque si la demande a DÉJÀ un refus émis (typedossier="R").
    // Vérification par lookup direct sur GET /api/refus/{noref}. Si 200 →
    // le noref courant est un refus → on interdit l'ouverture d'une attestation.
    const currentRef = (formData.no_reference || currentDossier?.no_ref || "").trim();
    if (currentRef) {
      try {
        const { data } = await axios.get(`${API}/refus/${encodeURIComponent(currentRef)}`);
        if (data && data.refus) {
          showPopup(
            `Un refus (No. ${currentRef}) a déjà été émis pour ce dossier. `
            + "Vous ne pouvez émettre une attestation d'admissibilité.",
            "warning", "Attestation"
          );
          return;
        }
      } catch (e) {
        // 404 → pas un refus → OK on continue
      }
    }
    // Parité legacy (Main.vb:735) : si la demande est REFUSÉE (adda670),
    // Refus de CONTRIBUER (adda675) ou Refus avec CONSULTATION (adda672),
    // on interdit l'accès aux Attestations.
    const decA = formData.decision || {};
    const aAdmis   = !!decA.admissible || formData.adda654 === 1;
    const aAttCond = !!decA.attestation_conditionnelle || formData.adda671 === 1;
    const aRefuse  = !!decA.inadmissible_financier || formData.adda670 === 1;
    const aRefCont = !!decA.refus_contribuer || formData.adda675 === 1;
    const aRefCons = !!decA.refus_consultation || formData.adda672 === 1;
    // Aucune décision cochée → même message que le Refus (parité OuvreRefus).
    if (!aAdmis && !aAttCond && !aRefuse && !aRefCont && !aRefCons) {
      showPopup("Vous devez prendre une décision !", "warning", "Admissibilité: Attestations");
      return;
    }
    if (aRefuse || aRefCont || aRefCons) {
      showPopup(
        "La demande étant refusée, vous ne pouvez avoir accès aux Attestations.",
        "warning", "Admissibilité: Attestations"
      );
      return;
    }
    // Ouverture inconditionnelle — le modal gère lui-même l'onglet par défaut
    // (Liste si attestations existent, sinon Demande d'aide juridique).
    setShowAttestation(true);
  };

  // Ouvrir modale Refus — parité `RefusStripButton_Click` (MDIParent2.vb:1504)
  // Prérequis legacy vérifiés côté UI :
  //   - Dossier ouvert (client + DAJ)
  //   - Décision cochée parmi Refuse (adda670) / Refus consult. (adda672) / Refus contrib. (adda675)
  //   - PAS Admis (adda654) ni Attestation conditionnelle (adda671)
  //   - Article 69 + Nature "CRIMINEL" bloque
  //   - Dossier enregistré (isDirty=false)
  const handleOuvrirRefus = async () => {
    const pkDaj = currentDossier?.pk_daj || currentDossier?.no_seq_daj || formData.no_sequence;
    const noclientCourant = formData.noclient || currentDossier?.noclient;
    if (!pkDaj || !noclientCourant) {
      showPopup("Aucun dossier n'est ouvert. Ouvrez un dossier avant d'accéder aux refus.", "warning", "Refus");
      return;
    }
    // Parité legacy : bloque si la demande a DÉJÀ une attestation émise
    // (typedossier="A"). Vérification par lookup GET /api/attestations/{noref}.
    // Si 200 → le noref courant est une attestation → on interdit le refus.
    const currentRef = (formData.no_reference || currentDossier?.no_ref || "").trim();
    if (currentRef) {
      try {
        const { data } = await axios.get(`${API}/attestations/${encodeURIComponent(currentRef)}`);
        if (data && data.attestation) {
          showPopup(
            `Une attestation d'admissibilité (No. ${currentRef}) a déjà été émise `
            + "pour ce dossier. Vous ne pouvez émettre un refus.",
            "warning", "Refus"
          );
          return;
        }
      } catch (e) {
        // 404 → pas une attestation → OK on continue
      }
    }
    // Décision cochée ? (parité OuvreRefus lignes 894-901)
    // Les états DECISIONS sont stockés dans `formData.decision` (nested object)
    // par `DecisionTab.jsx`. Mapping legacy :
    //   admissible                 → adda654 chkAdmis
    //   attestation_conditionnelle → adda671 chkAttesCond
    //   inadmissible_financier     → adda670 chkRefuse ("Refusé")
    //   refus_consultation         → adda672 chkRefusConsult
    //   refus_contribuer           → adda675 chkRefusContrib
    // On fait un fallback vers les alias plats pour rétrocompat (anciens formats).
    const dec = formData.decision || {};
    const admis   = !!dec.admissible || formData.adda654 === 1;
    const attCond = !!dec.attestation_conditionnelle || formData.adda671 === 1;
    const refuse  = !!dec.inadmissible_financier || formData.adda670 === 1;
    const refCons = !!dec.refus_consultation || formData.adda672 === 1;
    const refCont = !!dec.refus_contribuer || formData.adda675 === 1;
    if (!admis && !attCond && !refuse && !refCons && !refCont) {
      showPopup("Vous devez prendre une décision !", "warning", "Admissibilité: Refus");
      return;
    }
    if (admis || attCond) {
      showPopup("La demande étant admise, vous ne pouvez avoir accès aux refus.", "warning", "Admissibilité: Refus");
      return;
    }
    // Article 69 + Criminel = bloqué (parité `Art69Crim` Validations.vb:485)
    // Le legacy VB.NET vérifie chkArt69 (adda661) + cboNatCateg="CRIMINEL".
    // Frontend : decision.refus_art_69 (checkbox du sub-tab Refus).
    const art69 = !!dec.refus_art_69 || formData.adda661 === 1;
    if (art69 && (formData.cboNatCateg || formData.categorie_nature || "").toUpperCase().startsWith("CRIMINEL")) {
      showPopup("Un refus en vertu de l'article 69 ne s'applique pas en matière criminelle. Veuillez vérifier !",
                "warning", "Admissibilité: Refus");
      return;
    }
    if (isDirty && editMode !== "vide") {
      showPopup("Vous devez enregistrer la demande d'admissibilité afin de produire un refus.",
                "warning", "Refus");
      return;
    }
    // Parité legacy MDIParent2.vb:1508-1544 — validation spécifique aux
    // dossiers d'Homologation (code nature commençant par "H") :
    //   • Si dossier "En suspens" + noRef vide + PAS d'inadmissibilité financière
    //     + PAS de motif de refus homologation → bloque avec message dédié.
    //   • Sinon si (funcInadFinOnly XOR bRefusHomolog) → bloque : un refus H
    //     ne peut être généré que sur inadmisFin OU homologation, jamais les 2
    //     ni aucun des deux.
    //   • Sinon → OuvreRefus() (comportement normal).
    const codeNature = ((formData.service?.code_nature || formData.adda510 || "")
                         .toString().trim().toUpperCase());
    if (codeNature.startsWith("H")) {
      let bRefusHomolog = false;
      try {
        const { data } = await axios.get(
          `${API}/dajs/${encodeURIComponent(pkDaj)}/refus-homolog-exists`);
        bRefusHomolog = !!(data && data.exists);
      } catch (e) {
        // Endpoint absent ou 404 → considère qu'il n'y a pas de motif H
        bRefusHomolog = false;
      }
      // funcInadFinOnly (parité MDIParent2.vb:1551) — RETURN TRUE si UNIQUEMENT
      // l'inadmissibilité financière est cochée parmi les 16 checkboxes refus.
      const inadmisFin = !!dec.refus_inadmis_financier;
      const otherRefusChecked = [
        'refus_service_nommement_exclu',
        'refus_non_vraisemblable', 'refus_peu_succes', 'refus_cout_deraisonnable',
        'refus_impossibilite_execution', 'refus_reglement_raisonnable',
        'refus_autres_services_disponibles',
        'refus_art_69', 'refus_service_non_couvert',
        'refus_appel_non_raisonnablement_fonde', 'refus_encontre_loi',
        'refus_disposition_bien', 'refus_renseignements_faux',
        'refus_renseignements_faux_inexacts', 'refus_autres_cas_70',
      ].some(k => !!dec[k]);
      const funcInadFinOnly = inadmisFin && !otherRefusChecked;

      // "En suspens" — dossier incomplet. Heuristique parité : addastatut contient "suspens".
      const statut = (formData.addastatut || formData.statut || "").toString().toLowerCase();
      const enSuspens = statut.includes("suspens");
      const noRefVide = !currentRef;

      if (enSuspens && noRefVide && !inadmisFin && !bRefusHomolog) {
        showPopup(
          "La demande est incomplète.\nVous ne pouvez accéder aux refus!\n\n"
          + "Un refus ne peut être généré que pour une demande comportant "
          + "soit une inadmissibilité financière OU un motif de refus inscrit "
          + "dans la section homologation.",
          "warning", "Avis");
        return;
      }
      // XOR strict : les 2 cas (aucun motif) et (les 2 motifs) sont interdits
      if ((funcInadFinOnly === false && bRefusHomolog === false) ||
          (funcInadFinOnly === true  && bRefusHomolog === true)) {
        showPopup(
          "Vous ne pouvez accéder aux refus!\n\n"
          + "Un refus ne peut être généré que pour une demande comportant "
          + "soit une inadmissibilité financière OU un motif de refus inscrit "
          + "dans la section homologation.",
          "warning", "Avis");
        return;
      }
    }
    setShowRefus(true);
  };

  // Ouvrir modale Homologation — parité `mnuOuvrirHomolog_Click`
  // (MDIParent2.vb:4072-4088).
  // Prérequis legacy vérifiés dans l'ordre exact :
  //   1. Code nature commence par "H" (parité `cboNatCateg_LostFocus`:4092)
  //   2. Dossier complété OU chkAttesCond coché
  //   3. Au moins une décision cochée
  //   4. Dossier enregistré (isDirty=false ET dossier existe)
  const handleOuvrirHomolog = () => {
    // (sous-menu Homologation retiré — ouverture directe en 1 clic)
    // 1. Code nature Homologation ?
    const codeNat = ((formData.service?.code_nature || formData.adda510 || "")
                       .toString().trim().toUpperCase());
    if (!codeNat.startsWith("H")) {
      showPopup(
        "Le menu Homologation n'est accessible que pour un dossier dont la "
        + "catégorie du service est Homologation (code nature commençant par H).",
        "warning", "Admissibilité: Homologation");
      return;
    }
    // 2. Décision cochée ? (parité legacy step 2 — au moins UNE case cochée)
    const dec = formData.decision || {};
    const admis   = !!dec.admissible || formData.adda654 === 1;
    const attCond = !!dec.attestation_conditionnelle || formData.adda671 === 1;
    const refuse  = !!dec.inadmissible_financier || formData.adda670 === 1;
    const refCons = !!dec.refus_consultation || formData.adda672 === 1;
    const refCont = !!dec.refus_contribuer || formData.adda675 === 1;
    if (!attCond && !admis && !refuse && !refCons && !refCont) {
      showPopup("Vous devez prendre une décision !", "warning",
                "Admissibilité: Homologation");
      return;
    }
    // 3. Dossier finalisé ? Legacy vérifie le titre "Complété" (état DAJ finalisé)
    //    ou chkAttesCond. Web equivalent : une décision décisive est cochée
    //    (Admis, AttestConditionnelle, ou un refus) → dossier considéré finalisé
    //    car il ne peut plus être en état "vide" ou "brouillon" incomplet.
    const finalise = attCond || admis || refuse || refCons || refCont;
    if (!finalise) {
      showPopup(
        "La demande est incomplète.\nVous ne pouvez accéder à l'Homologation !",
        "warning", "Admissibilité: Homologation");
      return;
    }
    // 4. Dossier enregistré ?
    if (isDirty && editMode !== "vide") {
      showPopup(
        "Vous devez enregistrer la demande d'admissibilité afin de produire "
        + "une Homologation.",
        "warning", "Homologation");
      return;
    }
    if (editMode === "vide") {
      showPopup("Aucun dossier n'est ouvert.", "warning", "Homologation");
      return;
    }
    setShowHomolog(true);
  };

  const checkDirtyThen = (action) => {
    if (isDirty && editMode !== 'vide') {
      setConfirmDirty({ isOpen: true, action });
    } else {
      action();
    }
  };

  const handleDirtySave = async () => {
    setConfirmDirty({ isOpen: false, action: null });
    await handleEnregistrer();
    // Après sauvegarde, exécuter l'action en attente
    if (confirmDirty.action) confirmDirty.action();
  };

  const handleDirtyDiscard = () => {
    const action = confirmDirty.action;
    setConfirmDirty({ isOpen: false, action: null });
    setIsDirty(false);
    if (action) action();
  };
  
  const closePopup = () => {
    setPopup({ ...popup, isOpen: false });
  };

  // Ferme le dossier en cours (retour à l'état vide)
  const fermerDossierCourant = () => {
    setFormData(getEmptyFormData());
    setCurrentClient(null); setCurrentDossier(null);
    setEditMode('vide'); setIsDirty(false);
    setCurrentTab('requerant'); setDossierDateModif(null);
  };

  // Supprime la demande DAJ ouverte. Règle métier : une demande ne peut être
  // supprimée que si AUCUN numéro de référence n'y est inscrit (la validation
  // est aussi appliquée côté serveur).
  const handleSupprimerDemande = async () => {
    const noseq = formData.no_sequence || currentDossier?.no_seq_daj || '';
    if (!noseq) {
      showPopup("Aucune demande n'est ouverte. Ouvrez une demande avant de la supprimer.",
                "warning", "Supprimer une demande");
      return;
    }
    const currentRef = (formData.no_reference || currentDossier?.no_ref || "").trim();
    if (currentRef) {
      showPopup(
        `Impossible de supprimer cette demande : un numéro de référence (No. ${currentRef}) `
        + "y est inscrit. Seule une demande sans numéro de référence peut être supprimée.",
        "warning", "Supprimer une demande"
      );
      return;
    }
    if (!window.confirm(
      "Voulez-vous vraiment supprimer définitivement cette demande d'aide juridique ?\n\n"
      + "Cette action est irréversible."
    )) return;
    try {
      await axios.delete(`${API}/daj/${encodeURIComponent(noseq)}`);
      fermerDossierCourant();
      loadStats();
      showPopup("La demande a été supprimée avec succès.", "success", "Supprimer une demande");
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message;
      if (status === 409) {
        showPopup(detail, "warning", "Supprimer une demande");
      } else if (status === 404) {
        showPopup("La demande est introuvable (déjà supprimée ?).", "warning", "Supprimer une demande");
      } else {
        showPopup(`Erreur lors de la suppression : ${detail}`, "error", "Supprimer une demande");
      }
    }
  };

  // Outils > Changer de bureau : si un dossier est ouvert, exiger la
  // sauvegarde + fermeture avant de permettre la bascule.
  const handleChangerBureau = () => {
    if (editMode !== 'vide') {
      setConfirmBureauSwitch(true);
    } else {
      setShowChangerBureau(true);
    }
  };

  const handleBureauSwitchSaveClose = async () => {
    setConfirmBureauSwitch(false);
    const ok = await handleEnregistrer();
    if (ok !== true) return; // sauvegarde échouée/incomplète : on ne ferme rien
    fermerDossierCourant();
    setShowChangerBureau(true);
  };
  
  // Donnees de reference (Static DB) — chargées DYNAMIQUEMENT depuis les
  // endpoints /api/reference/{categories,provinces}. Ne JAMAIS hardcoder :
  // le contenu des tables `Static.dbo.CatReq` et `Static.dbo.province` peut
  // évoluer côté administration de l'aide juridique.
  //
  // Legacy ref : `MDIParent2.vb:330-358` — `cboProv/cboProvConj/cboProvLien*
  // /cboCatReq.DataSource = liste*.Load(gConStaticCourant)`.
  const [categories, setCategories] = useState([]);
  const [provinces, setProvinces] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catRes, provRes] = await Promise.all([
          axios.get(`${API}/reference/categories`),
          axios.get(`${API}/reference/provinces`),
        ]);
        if (cancelled) return;
        // catreq : { code, description } → { value: code, label: description }
        setCategories((catRes.data || []).map(r => ({
          value: (r.code || '').trim(),
          label: (r.description || '').trim(),
        })));
        // province : { code, description, codeang, descang } → { value: code, label: code }
        // Le legacy affiche le code (ex "CB", "IPE") dans le combo.
        setProvinces((provRes.data || []).map(r => ({
          value: (r.code || '').trim(),
          label: (r.code || '').trim(),
          description: (r.description || '').trim(),
        })));
      } catch (e) {
        console.error('Erreur chargement reference categories/provinces', e);
        if (!cancelled) {
          setCategories([]);
          setProvinces([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const emptyLien = {
    type_lien: '', nom: '', prenom: '', date_naissance: '', sexe: '',
    nas: '', interets_opposes: false, adresse: '', ville: '', province: 'QC',
    code_postal: '', telephone_residence: '', telephone_bureau: '', telephone_ext: '', courriel: ''
  };

  const getEmptyFormData = () => ({
    no_dossier: '', no_reference: '', region: defaultRegion, bureau: defaultBureau, no_sequence: '',
    adda235: 0,
    date_demande: todayLocal(),
    nom: '', prenom: '', date_naissance: '', sexe: '1', langue: '1', nas: '',
    sed: '', no_bande: '', no_aide_sociale: '', montant_aide_sociale: 0,
    adresse: '', ville: '', code_postal: '', province: 'QC',
    telephone_residence: '', telephone_bureau: '', telephone_ext: '', cellulaire: '', courriel: '',
    categorie_requerant: '01', nb_enfants: 0, revision: false, reciprocite: false, transfert: false, region_eloignee: false,
    transfert_source_seq: '', transfert_source_reg: '', transfert_source_bur: '', transfert_cible_reg: '', transfert_cible_bur: '', reciprocite_province: '', revision_note: '',
    notes: '',
    notes_non_imprimees: '',
    revenus: {
      emploi_req: 0, assurance_emploi_req: 0, securite_revenu_req: 0,
      csst_req: 0, saaq_req: 0, pension_vieillesse_req: 0,
      pension_alimentaire_req: 0, pension_alimentaire_enfants_req: 0, rrq_rpc_req: 0, autres_revenus_req: 0,
      emploi_fam: 0, assurance_emploi_fam: 0, securite_revenu_fam: 0,
      csst_fam: 0, saaq_fam: 0, pension_vieillesse_fam: 0,
      pension_alimentaire_fam: 0, pension_alimentaire_enfants_fam: 0, rrq_rpc_fam: 0, autres_revenus_fam: 0,
      famille_incluse: true, annee_reference: '', annee_type: 'precedente', raison_annee_courante: '',
      deficience: 0, frais_garde: 0, pension_alimentaire_payee: 0, frais_scolaires: 0,
      occupation: '', employeur: '', nom_employeur_1: '', adr_employeur_1: '', nom_employeur_2: '', adr_employeur_2: ''
    },
    actifs: {
      residence_req: 0, autres_immeubles_req: 0, regime_retraite_req: 0,
      autres_biens_req: 0, moins_dettes_req: 0,
      argent_especes_req: 0, depot_terme_req: 0, autres_liquidites_req: 0,
      residence_fam: 0, autres_immeubles_fam: 0, regime_retraite_fam: 0,
      autres_biens_fam: 0, moins_dettes_fam: 0,
      argent_especes_fam: 0, depot_terme_fam: 0, autres_liquidites_fam: 0
    },
    conjoint: {
      nom: '', prenom: '', date_naissance: '', sexe: '', nas: '',
      art_6_ou_7: '', interets_opposes: false,
      adresse: '', ville: '', province: 'QC', code_postal: '',
      telephone_residence: '', telephone_bureau: '', telephone_ext: '', courriel: ''
    },
    lien1: { ...emptyLien },
    lien2: { ...emptyLien },
    decision: null,
    service: {
      categorie: '', nature: '', service_nom: '', code_nature: '', article: '',
      nature_accessoire: '', fondement_droit: '', no_dossier_cour: '',
      type_dossier_sommaire: false, en_demande: 2, date_signature_req: '',
      meme_affaire: false, meme_affaire_ref: '', contribution_max: 0,
      cout_reel: 0, montant_deja_verse: 0, temoin: '', code_tech: '', nom_tech: '', notes_service: '',
      gcr_recouv: false, gcr_date: '', gcr_montant: 0
    }
  });

  // Etat du formulaire
  const [formData, setFormData] = useState(getEmptyFormData());

  // === Info manquante : fetch autonome + application indicateur cellule ===
  // Cf. déclaration `missingItems` en haut du composant. Ces useEffects
  // sont placés après `formData` pour éviter les TDZ errors sur les deps.
  useEffect(() => {
    const noseq = formData?.no_sequence || currentDossier?.no_seq_daj || '';
    if (!noseq) { setMissingItems([]); return; }
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/daj/${encodeURIComponent(noseq)}/info-manquante-check`)
      .then(({ data }) => {
        if (!cancelled) {
          setMissingItems(data?.items || []);
          setDossierStatut({
            label: data?.statut || '',
            code: data?.statut_code_r ?? 0,
            addastatut: (data?.addastatut || '').trim(),
          });
        }
      })
      .catch(() => { /* silencieux */ });
    return () => { cancelled = true; };
  }, [formData?.no_sequence, currentDossier?.no_seq_daj, isDirty]);

  useEffect(() => {
    // Nettoyage : retire l'attribut de tous les éléments avant réapplication
    document.querySelectorAll('[data-info-manquante="true"]').forEach(el => {
      el.removeAttribute('data-info-manquante');
    });
    if (!missingItems || missingItems.length === 0) return;
    const applyIndicators = () => {
      for (const it of missingItems) {
        if (!it.field || it.tab !== currentTab) continue;
        const f = it.field;
        const fDash = f.replace(/_/g, '-');
        const selectors = [
          `[data-testid="input-${f}"]`,
          `[data-testid="input-${fDash}"]`,
          `[data-testid="${f}"]`,
          `[data-testid="${fDash}"]`,
          `[name="${f}"]`,
        ];
        for (const sel of selectors) {
          document.querySelectorAll(sel).forEach(el => {
            // Ne jamais marquer « requis » un champ qui contient déjà une vraie
            // valeur à l'écran (saisie non encore enregistrée) — évite la
            // confusion signalée par l'utilisateur (téléphone rempli + requis).
            const val = (el.value || '').trim();
            if (val !== '') return;
            el.setAttribute('data-info-manquante', 'true');
          });
        }
      }
    };
    applyIndicators();
    const t1 = setTimeout(applyIndicators, 100);
    const t2 = setTimeout(applyIndicators, 400);
    // Retire l'indicateur dès que l'utilisateur saisit une valeur dans le champ
    const onInput = (e) => {
      const el = e.target;
      if (el?.getAttribute?.('data-info-manquante') === 'true' && (el.value || '').trim() !== '') {
        el.removeAttribute('data-info-manquante');
      }
    };
    document.addEventListener('input', onInput);
    return () => { clearTimeout(t1); clearTimeout(t2); document.removeEventListener('input', onInput); };
  }, [missingItems, currentTab]);


  // Charger les donnees initiales
  useEffect(() => { loadStats(); }, []);

  // Auto-synchronise le champ NbEnfant de l'onglet Requérant avec le nombre
  // de liens de type "Enfant" présents dans l'onglet Liens. Se déclenche à chaque
  // fois que la liste des liens change (ajout / suppression / édition du type).
  useEffect(() => {
    const nbEnfants = liensListe.filter(l => isEnfantType(l.type_lien)).length;
    setFormData(prev => {
      if ((prev.nb_enfants || 0) === nbEnfants) return prev;
      return { ...prev, nb_enfants: nbEnfants };
    });
  }, [liensListe]);

  const loadStats = async () => {
    try { const response = await axios.get(`${API}/stats`); setStats(response.data); } catch (error) { console.error('Erreur chargement stats:', error); }
  };

  const updateFormData = (field, value) => { setFormData(prev => ({ ...prev, [field]: value })); setIsDirty(true); };
  const updateRevenus = (field, value) => { setFormData(prev => ({ ...prev, revenus: { ...prev.revenus, [field]: typeof value === 'boolean' ? value : (parseFloat(value) || 0) } })); setIsDirty(true); };
  const updateActifs = (field, value) => { setFormData(prev => ({ ...prev, actifs: { ...prev.actifs, [field]: parseFloat(value) || 0 } })); setIsDirty(true); };
  const updateConjoint = (field, value) => { setFormData(prev => ({ ...prev, conjoint: { ...prev.conjoint, [field]: value } })); setIsDirty(true); };

  // Equivalent legacy MisJrCal(True) côté revenus (CodeDaj.vb:351-358) :
  // remet à zéro tous les revenus consolidés du conjoint (adda320-adda338)
  // après suppression des lignes infofin sourceid='C'. Appelé depuis
  // ConjointTab.confirmArt67 lorsque l'utilisateur confirme la sélection
  // d'un art. 7(1) ou 7(2).
  const clearConjointRevenus = () => {
    setFormData(prev => ({
      ...prev,
      revenus: {
        ...prev.revenus,
        emploi_fam: 0, assurance_emploi_fam: 0, securite_revenu_fam: 0,
        csst_fam: 0, saaq_fam: 0, pension_vieillesse_fam: 0,
        pension_alimentaire_fam: 0, pension_alimentaire_enfants_fam: 0,
        rrq_rpc_fam: 0, autres_revenus_fam: 0,
      }
    }));
    setIsDirty(true);
  };
  const updateService = (field, value) => { setFormData(prev => ({ ...prev, service: { ...prev.service, [field]: value } })); setIsDirty(true); };

  // Décochage "Même affaire que" (parité legacy CodeInfoFin.vb:507)
  // ---------------------------------------------------------------
  // Quand l'utilisateur DÉCOCHE la case "Même affaire que", il faut :
  //  1. Effacer toutes les infos reportées depuis le dossier référencé
  //     (ref, contribution_max, frais_admin, badge "Repris de XXX").
  //  2. Recalculer la contribution max sur base des informations
  //     financières du dossier COURANT via /api/calcul-admissibilite —
  //     équivalent legacy `ContMax()` appelé automatiquement quand
  //     `chkMemAffair.Checked <> 1` (cf. CodeInfoFin.vb:507).
  const handleUncheckMemeAffaire = async () => {
    // Étape 1 : reset immédiat des champs reportés (feedback UI instantané)
    setFormData(prev => ({
      ...prev,
      service: {
        ...prev.service,
        meme_affaire: false,
        meme_affaire_ref: '',
        contribution_max: 0,
        frais_admin: 0,
      },
      decision: {
        ...(prev.decision || {}),
        contribution_max: 0,
        frais_admin: 0,
        avec_contribution: false,
        gratuit: false,
        message: '',
      },
    }));
    setIsDirty(true);

    // Étape 2 : recalcul basé sur la demande courante.
    // On skip si la catégorie ou la date de demande manquent (nouveau
    // dossier vide) — dans ce cas les valeurs restent à zéro.
    if (!formData.categorie_requerant || !formData.date_demande) return;
    try {
      const response = await axios.post(
        `${API}/calcul-admissibilite?categorie=${formData.categorie_requerant}` +
        `&date_demande=${formData.date_demande}` +
        `&region_eloignee=${formData.region_eloignee}` +
        `&reciprocite=${formData.reciprocite}`,
        { revenus: formData.revenus, actifs: formData.actifs }
      );
      // Fusion prudente : on garde les autres champs de decision et on n'écrase
      // que ce qui est calculé par le moteur d'admissibilité.
      setFormData(prev => ({
        ...prev,
        decision: response.data,
        service: {
          ...prev.service,
          contribution_max: response.data.contribution_max || 0,
          frais_admin: response.data.frais_admin || 0,
        },
      }));
    } catch (e) {
      // Silencieux : les champs restent à zéro (déjà set à l'étape 1).
      console.warn('Recalcul contribution max échoué:', e.message);
    }
  };

  // Onglet Décision : update d'un champ (parité legacy `chk*_CheckedChanged`).
  // Règle métier auto : Gratuit ↔ Avec contribution sont exclusifs (legacy
  // CodeDaj.vb:2186-2188). De même, Admis ↔ Refusé.
  const updateDecision = (field, value) => {
    setFormData(prev => {
      const dec = { ...(prev.decision || {}), [field]: value };
      if (field === 'gratuit' && value) dec.avec_contribution = false;
      if (field === 'avec_contribution' && value) dec.gratuit = false;
      if (field === 'admissible' && value) dec.inadmissible_financier = false;
      if (field === 'inadmissible_financier' && value) dec.admissible = false;
      return { ...prev, decision: dec };
    });
    setIsDirty(true);
  };

  // Copier adresse requerant -> conjoint
  const handleMemeAdresseConjoint = () => {
    setFormData(prev => ({
      ...prev,
      conjoint: { ...prev.conjoint, adresse: prev.adresse, ville: prev.ville, province: prev.province, code_postal: prev.code_postal, telephone_residence: prev.telephone_residence, telephone_bureau: prev.telephone_bureau, telephone_ext: prev.telephone_ext }
    }));
  };

  // Verif. intérêts opposés (équivalent legacy btnVerifIntOppose_Click /
  // MDIParent2.vb:1075-1100 + ChercheIntOpp dans CodeDaj.vb:1043-1067).
  // Cherche les dossiers où le couple (nom, prénom) du conjoint courant
  // est le REQUÉRANT PRINCIPAL d'un autre DAJ → détecte un conflit
  // d'intérêts opposés.
  const [verifIntOppResults, setVerifIntOppResults] = useState(null);
  const [verifIntOppLoading, setVerifIntOppLoading] = useState(false);

  const handleVerifInteretsOpposes = async () => {
    const nom = (formData.conjoint.nom || '').trim();
    const prenom = (formData.conjoint.prenom || '').trim();
    if (!nom || !prenom) {
      showPopup(
        'Vous devez écrire au moins le nom et le prénom du conjoint',
        'info',
        'Recherche des intérêts opposés'
      );
      return;
    }
    setVerifIntOppLoading(true);
    try {
      const res = await axios.get(`${API}/intoppose/search`, {
        params: { nom, prenom }
      });
      const data = res.data || { count: 0, results: [] };
      if (data.count === 0) {
        showPopup(
          `Il n'y a pas de demande dont ${prenom} ${nom} est le requérant principal`,
          'info',
          'Recherche des intérêts opposés'
        );
      } else {
        setVerifIntOppResults({ ...data, nom, prenom });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Erreur réseau';
      showPopup(msg, 'error', 'Recherche des intérêts opposés');
    } finally {
      setVerifIntOppLoading(false);
    }
  };

  const handleSupprimerConjoint = async () => {
    // Equivalent legacy : suppression complète du conjoint et de TOUT ce qui
    // lui est rattaché pour CE dossier — ne pas laisser de revenus, actifs
    // ou lignes infofin orphelines (sinon le calcul d'admissibilité serait
    // faussé). Conformément au comportement attendu par l'utilisateur.
    const currentNoSeq = formData.no_sequence || currentDossier?.no_seq_daj || '';

    // 1) DELETE des lignes infofin du conjoint (sourceid='C') si le dossier
    //    existe déjà en BD. Pour les nouveaux dossiers (pas encore sauvegardés),
    //    il n'y a rien à supprimer côté BD ; le state local sera nettoyé en (2).
    if (currentNoSeq) {
      try {
        await axios.delete(`${API}/dossier/${encodeURIComponent(currentNoSeq)}/infofin/conjoint`);
      } catch (err) {
        console.error('Erreur DELETE infofin conjoint lors de Supprimer:', err);
      }
    }

    // 2) Nettoyer le state local de TOUTES les données rattachées au conjoint :
    //    - conjoint (nom, adresse, art.7, etc.)
    //    - revenus famille (adda320-338)
    //    - actifs famille (adda440-466)
    //    - famille_incluse repassé à false (cohérent : pas de conjoint = pas
    //      d'inclusion famille possible)
    setFormData(prev => ({
      ...prev,
      conjoint: {
        nom: '', prenom: '', date_naissance: '', sexe: '', nas: '',
        art_6_ou_7: '', interets_opposes: false,
        adresse: '', ville: '', province: 'QC', code_postal: '',
        telephone_residence: '', telephone_bureau: '', telephone_ext: '', courriel: ''
      },
      revenus: {
        ...prev.revenus,
        famille_incluse: false,
        emploi_fam: 0, assurance_emploi_fam: 0, securite_revenu_fam: 0,
        csst_fam: 0, saaq_fam: 0, pension_vieillesse_fam: 0,
        pension_alimentaire_fam: 0, pension_alimentaire_enfants_fam: 0,
        rrq_rpc_fam: 0, autres_revenus_fam: 0,
      },
      actifs: {
        ...prev.actifs,
        residence_fam: 0, autres_immeubles_fam: 0, regime_retraite_fam: 0,
        autres_biens_fam: 0, moins_dettes_fam: 0,
        argent_especes_fam: 0, depot_terme_fam: 0, autres_liquidites_fam: 0,
      },
    }));
    // Marquer dirty pour que Enregistrer déclenche le POST qui DELETE
    // la row clliens conjoint côté backend (et écrira les *_fam à 0).
    setIsDirty(true);
  };

  // ========== GESTION DES LIENS FAMILIAUX ==========
  const loadLiensFromServer = async () => {
    if (!formData.noclient) { setLiensListe([]); return; }
    setIsLoadingLiens(true);
    try {
      const response = await axios.get(`${API}/clients/${formData.noclient}/liens`);
      const liensSansConjoint = (response.data || []).filter(l => !isConjointType(l.type_lien));
      setLiensListe(liensSansConjoint);
    } catch (error) { console.error('Erreur chargement liens:', error); setLiensListe([]); }
    setIsLoadingLiens(false);
  };

  const handleNouveauLien = () => {
    setLienEnEdition({ id: null, type_lien: '', nom: '', prenom: '', date_naissance: '', sexe: '1', nas: '', interets_opposes: false, adresse: '', ville: '', province: 'QC', code_postal: '', telephone_residence: '', telephone_bureau: '', telephone_ext: '', courriel: '' });
  };

  const handleEditerLien = (lien) => { setLienEnEdition({ ...lien, sexe: String(lien.sexe || '1') }); };

  const handleSauvegarderLien = async () => {
    if (!lienEnEdition) {
      setLienEnEdition({ id: null, type_lien: '', nom: '', prenom: '', date_naissance: '', sexe: '1', nas: '', interets_opposes: false, adresse: '', ville: '', province: 'QC', code_postal: '', telephone_residence: '', telephone_bureau: '', telephone_ext: '', courriel: '' });
    }
    if (!lienEnEdition?.type_lien || !lienEnEdition?.nom || !lienEnEdition?.prenom) {
      showPopup('Le type de lien, le nom et le prénom sont obligatoires.', 'error', 'Champs requis'); return;
    }
    try {
      const lienData = { ...lienEnEdition, noclient: formData.noclient, noseqdaj: formData.no_sequence, sexe: parseInt(lienEnEdition.sexe) || 1 };
      if (lienEnEdition.id) {
        await axios.put(`${API}/liens/${lienEnEdition.id}`, lienData);
      } else {
        await axios.post(`${API}/liens`, lienData);
      }
      setLienEnEdition({ id: null, type_lien: '', nom: '', prenom: '', date_naissance: '', sexe: '1', nas: '', interets_opposes: false, adresse: '', ville: '', province: 'QC', code_postal: '', telephone_residence: '', telephone_bureau: '', telephone_ext: '', courriel: '' });
      await loadLiensFromServer();
      await updateLiens1et2();
    } catch (error) { console.error('Erreur sauvegarde lien:', error); showPopup('Erreur lors de la sauvegarde du lien.', 'error', 'Erreur'); }
  };

  const handleSupprimerLienServeur = async (lienId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce lien?')) return;
    try {
      await axios.delete(`${API}/liens/${lienId}`);
      showPopup('Lien supprimé avec succès.', 'success', 'Succès');
      await loadLiensFromServer();
      await updateLiens1et2();
    } catch (error) { console.error('Erreur suppression lien:', error); showPopup('Erreur lors de la suppression du lien.', 'error', 'Erreur'); }
  };

  const handleDefinirCommeLienExclusif = (lien, lienNum) => {
    const lienData = { type_lien: lien.type_lien, nom: lien.nom, prenom: lien.prenom, date_naissance: lien.date_naissance, sexe: String(lien.sexe), nas: lien.nas, interets_opposes: lien.interets_opposes, adresse: lien.adresse, ville: lien.ville, province: lien.province || 'QC', code_postal: lien.code_postal, telephone_residence: lien.telephone_residence, telephone_bureau: lien.telephone_bureau, telephone_ext: lien.telephone_ext, courriel: lien.courriel };
    const otherLienNum = lienNum === 1 ? 2 : 1;
    const otherLien = lienNum === 1 ? formData.lien2 : formData.lien1;
    const hasOtherLienOnSameLine = otherLien.nom === lien.nom && otherLien.prenom === lien.prenom && otherLien.date_naissance === lien.date_naissance;
    setFormData(prev => ({
      ...prev,
      [lienNum === 1 ? 'lien1' : 'lien2']: lienData,
      ...(hasOtherLienOnSameLine ? { [otherLienNum === 1 ? 'lien1' : 'lien2']: { ...emptyLien } } : {})
    }));
  };

  const updateLiens1et2 = async () => {
    const response = await axios.get(`${API}/clients/${formData.noclient}/liens`);
    const liensSansConjoint = (response.data || []).filter(l => !isConjointType(l.type_lien));
    const mapLien = (d) => d.nom ? { type_lien: d.type_lien || '', nom: d.nom || '', prenom: d.prenom || '', date_naissance: d.date_naissance || '', sexe: String(d.sexe || ''), nas: d.nas || '', interets_opposes: d.interets_opposes || false, adresse: d.adresse || '', ville: d.ville || '', province: d.province || 'QC', code_postal: d.code_postal || '', telephone_residence: d.telephone_residence || '', telephone_bureau: d.telephone_bureau || '', telephone_ext: d.telephone_ext || '', courriel: d.courriel || '' } : { ...emptyLien };

    // Cherche le lien correspondant dans la liste serveur (par nom/prenom/date_naissance).
    // Retourne la VERSION SERVEUR — c'est crucial pour propager les modifs récentes
    // (ex: décochage de "intérêts opposés") au formulaire principal.
    const findRefreshed = (lien) => {
      if (!lien.nom) return null;
      return liensSansConjoint.find(l =>
        l.nom === lien.nom &&
        l.prenom === lien.prenom &&
        l.date_naissance === lien.date_naissance
      ) || null;
    };

    setFormData(prev => {
      let newLien1 = prev.lien1;
      let newLien2 = prev.lien2;

      // Rafraîchir lien1 : s'il existe encore en BD, prendre la version serveur.
      // Sinon le vider (il a été supprimé).
      if (newLien1.nom) {
        const fresh = findRefreshed(newLien1);
        newLien1 = fresh ? mapLien(fresh) : { ...emptyLien };
      }
      // Idem pour lien2
      if (newLien2.nom) {
        const fresh = findRefreshed(newLien2);
        newLien2 = fresh ? mapLien(fresh) : { ...emptyLien };
      }

      // Trouver les liens non encore assignés
      const isAssigned = (l) =>
        (newLien1.nom === l.nom && newLien1.prenom === l.prenom && newLien1.date_naissance === l.date_naissance) ||
        (newLien2.nom === l.nom && newLien2.prenom === l.prenom && newLien2.date_naissance === l.date_naissance);

      const liensNonAssignes = liensSansConjoint.filter(l => !isAssigned(l));

      // Si lien1 est vide et il y a des liens non assignés, prendre le premier
      if (!newLien1.nom && liensNonAssignes.length > 0) {
        newLien1 = mapLien(liensNonAssignes[0]);
        liensNonAssignes.shift();
      }

      // Si lien2 est vide et il y a encore des liens non assignés, prendre le suivant
      if (!newLien2.nom && liensNonAssignes.length > 0) {
        newLien2 = mapLien(liensNonAssignes[0]);
      }

      return { ...prev, lien1: newLien1, lien2: newLien2 };
    });
  };

  // ========== ACTIONS DOSSIER ==========
  const handleNouveau = () => {
    checkDirtyThen(() => {
      fermerDossierCourant();     // vider le dossier en cours après validation save/annuler
      setDossierDateModif(null);
      setShowModeDemande(true);   // choix Conditionnelle / Entrevue AVANT l'identification
    });
  };

  const handleModeDemandeChoisi = (mode) => {
    setModeDemande(mode);
    setShowModeDemande(false);
    setNouveauForm({ nom: '', prenom: '', date_naissance: '', nas: '' });
    setDuplicateClients([]); setShowDuplicateList(false); setShowNouveauModal(true);
  };

  // Selon le mode choisi : Conditionnelle / Régulière → création directe ;
  // Entrevue → questionnaire de vérification. La Régulière se comporte comme
  // l'Entrevue (recherche de doublons + préremplissage) mais NE démarre PAS
  // l'entrevue : elle va directement à la création de la demande.
  const procederCreation = async (pending) => {
    if (modeDemande === 'conditionnelle') {
      const result = pending.type === 'existing'
        ? await initializeDossierForExistingClient(pending.client)
        : await initializeNewDossier(nouveauForm.nom, nouveauForm.prenom, nouveauForm.date_naissance, nouveauForm.nas);
      if (result) {
        setSansEntrevue(true);
        // Mode Conditionnelle → pré-cocher « Attestation conditionnelle » (adda671).
        setFormData(prev => ({
          ...prev,
          decision: { ...(prev.decision || {}), attestation_conditionnelle: true },
        }));
        setIsDirty(true);
      }
    } else {
      // Modes Entrevue et Régulière : questionnaire de vérification d'abord.
      // Exception : requérant de moins de 18 ans → pas de questionnaire.
      // PARITÉ LEGACY : âge décisif à la DATE DE LA DEMANDE. À la création, la
      // date de demande du nouveau dossier vaut le jour (todayLocal()).
      if (estMineur(nouveauForm.date_naissance, todayLocal())) {
        if (modeDemande === 'reguliere') {
          await creerDemandeReguliere(pending, null, { mineur: true });
        } else {
          await creerEntrevue(pending, null, { mineur: true });
        }
      } else {
        setPendingNouveau(pending);   // ouvre le questionnaire de vérification
      }
    }
  };

  // Vrai si la date de naissance correspond à une personne de moins de 18 ans.
  // PARITÉ LEGACY (MoinsDe18Ans) : l'âge décisif est calculé à la DATE DE LA
  // DEMANDE (adda140 / DateDemand), pas à la date du jour. `refDate` par défaut =
  // aujourd'hui pour les appels à la création (où la date de demande vaut le jour).
  const estMineur = (ddn, refDate) => {
    if (!ddn) return false;
    const d = new Date(ddn);
    if (isNaN(d.getTime())) return false;
    const ref = refDate ? new Date(refDate) : new Date();
    if (isNaN(ref.getTime())) return false;
    let age = ref.getFullYear() - d.getFullYear();
    const m = ref.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && ref.getDate() < d.getDate())) age -= 1;
    return age < 18;
  };

  // Vrai s'il existe au moins un montant financier (revenu ou actif) non nul.
  const hasAnyFinancial = (fd) => {
    const anyNum = (obj) => Object.values(obj || {}).some(v => typeof v === 'number' && v !== 0);
    return anyNum(fd.revenus) || anyNum(fd.actifs);
  };

  // Met à zéro tous les montants numériques Revenus + Actifs, en conservant les
  // champs texte/booléens (famille_incluse, annee_reference, occupation, etc.).
  const zeroFinancialAmounts = (fd) => {
    const zeroNums = (obj) => Object.fromEntries(
      Object.entries(obj || {}).map(([k, v]) => [k, typeof v === 'number' ? 0 : v])
    );
    return { ...fd, revenus: zeroNums(fd.revenus), actifs: zeroNums(fd.actifs) };
  };

  // À l'ouverture d'un dossier (changement de n° séquence), reflète l'état mineur :
  // désactive silencieusement les onglets Revenus/Actifs si le requérant est mineur
  // à la date de la demande. Pas d'avertissement ni d'effacement ici (silencieux).
  useEffect(() => {
    setRevenuActifDisabled(estMineur(formData.date_naissance, formData.date_demande));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.no_sequence]);

  // Si les onglets Revenus/Actifs viennent d'être désactivés alors qu'ils étaient
  // affichés, bascule sur l'onglet Requérant.
  useEffect(() => {
    if (revenuActifDisabled && (currentTab === 'revenus' || currentTab === 'actifs')) {
      setCurrentTab('requerant');
    }
  }, [revenuActifDisabled, currentTab]);

  // Équivalent du `Leave` de la date de naissance Legacy (frmchild → ValidInfoFin).
  const handleDateNaissanceLeave = () => {
    const ddn = formData.date_naissance;
    const minor = ddn ? estMineur(ddn, formData.date_demande) : false;
    if (minor) {
      // Toujours avertir au passage à l'état mineur (parité Legacy "Mineur").
      if (!revenuActifDisabled) {
        setConfirmMineur({ isOpen: true, hasFinancial: hasAnyFinancial(formData) });
      }
    } else {
      // Majeur : si les onglets étaient désactivés pour cause de minorité, les
      // réactiver et informer l'utilisateur (parité Legacy "Majeur").
      if (revenuActifDisabled) {
        setRevenuActifDisabled(false);
        showPopup('Vous avez maintenant accès aux informations financières ...', 'info', 'Informations financières');
      }
    }
  };

  // Réponse "Oui" à l'avertissement mineur : efface les montants financiers et
  // désactive les onglets Revenus/Actifs. Supprime aussi les lignes infofin de
  // cette demande (parité Legacy SuppToutInfoFin()).
  const handleMineurConfirmOui = async () => {
    setFormData(prev => zeroFinancialAmounts(prev));
    setRevenuActifDisabled(true);
    setIsDirty(true);
    setConfirmMineur({ isOpen: false });
    const noseq = formData.no_sequence || currentDossier?.no_seq_daj || '';
    if (noseq) {
      try {
        await axios.delete(`${API}/dossier/${noseq}/infofin`);
      } catch (err) {
        console.warn('Suppression infofin (mineur) échouée :', err);
      }
    }
  };

  // Réponse "Non" : efface la date de naissance saisie (parité Legacy).
  const handleMineurConfirmNon = () => {
    setFormData(prev => ({ ...prev, date_naissance: '' }));
    setRevenuActifDisabled(false);
    setIsDirty(true);
    setConfirmMineur({ isOpen: false });
  };


  const handleNouveauSearch = async () => {
    if (!nouveauForm.nom.trim() || !nouveauForm.prenom.trim() || !nouveauForm.date_naissance) {
      showPopup('Veuillez remplir tous les champs obligatoires (Nom, Prénom, Date de naissance).', 'error', 'Champs requis'); return;
    }
    // Modes Entrevue et Régulière : « déjà venu » se vérifie dans la table FIR ;
    // les champs sont préremplis depuis la table client si le requérant existe,
    // sinon depuis l'entrevue FIR antérieure (structure JSON).
    if (modeDemande === 'entrevue' || modeDemande === 'reguliere') {
      setIsSearchingDuplicates(true);
      try {
        const [clientRes, firRes] = await Promise.all([
          axios.post(`${API}/clients/search-duplicates`, { nom: nouveauForm.nom.trim(), prenom: nouveauForm.prenom.trim(), date_naissance: nouveauForm.date_naissance, nas: nouveauForm.nas.replace(/-/g, '').trim() || null }),
          axios.get(`${API}/fir/rechercher`, { params: { nom: nouveauForm.nom.trim(), prenom: nouveauForm.prenom.trim(), ddn: nouveauForm.date_naissance } }),
        ]);
        const clients = (clientRes.data.found && clientRes.data.clients.length > 0) ? clientRes.data.clients : [];
        const priorFir = (firRes.data || [])[0] || null;
        if (clients.length > 0) {
          // Un ou plusieurs clients existants trouvés → demander lequel choisir
          // (ou créer un nouveau) via la liste de sélection.
          setPendingPriorFir(priorFir);
          setDuplicateClients(clients);
          setShowDuplicateList(true);
        } else {
          setShowNouveauModal(false);
          await procederCreation({ type: 'new', priorFir });
        }
      } catch (error) {
        console.error('Erreur recherche entrevue:', error);
        setShowNouveauModal(false);
        await procederCreation({ type: 'new' });
      } finally { setIsSearchingDuplicates(false); }
      return;
    }
    // Mode Conditionnelle : recherche de doublons dans la table client.
    setIsSearchingDuplicates(true);
    try {
      const response = await axios.post(`${API}/clients/search-duplicates`, { nom: nouveauForm.nom.trim(), prenom: nouveauForm.prenom.trim(), date_naissance: nouveauForm.date_naissance, nas: nouveauForm.nas.replace(/-/g, '').trim() || null });
      if (response.data.found && response.data.clients.length > 0) { setPendingPriorFir(null); setDuplicateClients(response.data.clients); setShowDuplicateList(true); }
      else { setShowNouveauModal(false); await procederCreation({ type: 'new' }); }
    } catch (error) { console.error('Erreur recherche doublons:', error); setShowNouveauModal(false); await procederCreation({ type: 'new' }); }
    finally { setIsSearchingDuplicates(false); }
  };

  const handleSelectExistingClient = (client) => {
    setShowNouveauModal(false);
    procederCreation({ type: 'existing', client, priorFir: pendingPriorFir });
    setPendingPriorFir(null);
  };

  const handleCreateNewAnyway = () => {
    setShowNouveauModal(false);
    procederCreation({ type: 'new', priorFir: pendingPriorFir });
    setPendingPriorFir(null);
  };

  // Prérempli les réponses FIR depuis le client existant (priorité) ou l'entrevue
  // FIR antérieure (structure JSON). Mappe vers les noms de champs du schéma FIR.
  const SEXE_LABEL = { 0: 'Féminin', 1: 'Masculin', 2: 'Non-binaire' };
  const buildFirPrefill = (pending) => {
    const c = pending?.client;
    if (c) {
      return {
        Nom: c.nom || '', Pr_nom: c.prenom || '', Date_de_naissance1: c.date_naissance || '',
        Sexe: SEXE_LABEL[parseInt(c.sexe)] || '', Num_ro_d_assurance_sociale1: c.nas || '',
        Adresse: c.adresse || '', Ville: c.ville || '', Province: c.province || 'QC',
        Code_postal: c.code_postal || '', T_l_phone: c.telephone_residence || '',
        T_l_phone_cellulaire: c.cellulaire || '', T_l_phone_autre: c.telephone_bureau || '',
        Adresse_courriel: c.courriel || '',
      };
    }
    const a = pending?.priorFir?.answers;
    if (a) {
      const keys = ['Nom', 'Pr_nom', 'Date_de_naissance1', 'Sexe', 'Num_ro_d_assurance_sociale1',
        'Adresse', 'Adresse1', 'N_Appartement1', 'Ville', 'Province', 'Code_postal',
        'T_l_phone', 'T_l_phone_cellulaire', 'T_l_phone_autre', 'Adresse_courriel',
        'Nom_Pr_nom_et_DDN_du_conjoint_e',
        'Noms_pr_noms_des_enfants_charge_compl_te_ou_en_garde_partag_e'];
      const out = {};
      keys.forEach(k => { const v = a[k]; if (v !== undefined && v !== null && v !== '') out[k] = v; });
      return out;
    }
    return {};
  };

  // Indicateur « LE CLIENT A ÉTÉ SÉLECTIONNÉ » selon le questionnaire de la demande ouverte
  useEffect(() => {
    const seq = formData.no_sequence;
    if (!seq) { setClientSelectionne(false); setSansEntrevue(false); return; }
    let annule = false;
    axios.get(`${API}/questionnaire/${encodeURIComponent(seq)}`, {
      params: { no_reg: formData.region || "", no_bur: formData.bureau || "" },
      withCredentials: true,
    })
      .then(r => { if (!annule) { setClientSelectionne(!!r.data.demande_docs); setSansEntrevue(false); } })
      .catch(() => { if (!annule) { setClientSelectionne(false); setSansEntrevue(true); } });
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.no_sequence]);

  // Révision (ChkRevision legacy) — cocher ouvre frmNoteRevision (note adda680)
  const handleRevisionToggle = (checked) => {
    if (checked) {
      setFormData(prev => ({ ...prev, revision: true }));
      setShowNoteRevision(true);
    } else {
      setFormData(prev => ({ ...prev, revision: false, revision_note: '' }));
    }
    setIsDirty(true);
  };

  const handleNoteRevisionRetour = (note) => {
    setFormData(prev => ({ ...prev, revision_note: note }));
    setShowNoteRevision(false);
    setIsDirty(true);
  };

  // Bureaux « région éloignée » par défaut (paramètre poste legacy gRegionEloignee)
  const loadBureauxEloignes = useCallback(() => {
    axios.get(`${API}/bureaux-eloignes`, { withCredentials: true })
      .then(r => setBureauxEloignes(new Set((r.data || []).map(b => `${b.noreg}-${b.nobur}`))))
      .catch(() => {});
  }, []);
  useEffect(() => { loadBureauxEloignes(); }, [loadBureauxEloignes]);
  const bureauEstEloigne = bureauxEloignes.has(`${defaultRegion}-${currentBureau || defaultBureau}`);

  // Réciprocité/Entente féd. (chkRecipro legacy). Les revenus/actifs sont
  // conservés et pris en compte dans le calcul d'admissibilité (demande utilisateur).
  const appliquerReciprocite = (provChoisie) => {
    setFormData(prev => ({
      ...prev, reciprocite: true, reciprocite_province: provChoisie || '',
    }));
    setIsDirty(true);
    setShowProvRecipro(false);
  };

  const handleReciproToggle = (checked) => {
    if (checked) {
      const prov = (formData.province || '').trim();
      if (prov === 'QC' || prov === '') {
        setShowProvRecipro(true);   // FrmProvRecipro : choisir l'autre province
      } else {
        appliquerReciprocite('');   // province ≠ QC : parité legacy (pas de fenêtre)
      }
    } else {
      setFormData(prev => ({ ...prev, reciprocite: false, reciprocite_province: '' }));
      setIsDirty(true);
    }
  };

  // Transfert (FrmTrans legacy) — cocher la case ouvre la fenêtre de transfert
  const handleTransfertToggle = (checked) => {
    if (checked) {
      if (!formData.no_sequence) {
        showPopup("Vous devez avoir une demande affichée à l'écran pour effectuer un transfert.", "warning", "Transfert");
        return;
      }
      setShowTransfertModal(true);
    } else {
      setFormData(prev => ({
        ...prev, transfert: false,
        transfert_source_seq: '', transfert_source_reg: '', transfert_source_bur: '',
        transfert_cible_reg: '', transfert_cible_bur: '',
      }));
      setIsDirty(true);
    }
  };

  const handleTransfertValide = (res) => {
    // Parité FrmTrans.BtnOK_Click : note non imprimée + champs source/cible
    const ident = `${formData.region || ''}-${formData.bureau || ''}-${formData.no_sequence || ''}`;
    const note = res.mode === 'vers'
      ? `Transfert de ${ident}/ Transfert vers ${res.regLabel} ${res.burLabel}`
      : `Transfert vers ${ident}/ Transfert de ${res.regLabel} ${res.burLabel}`;
    setFormData(prev => ({
      ...prev, transfert: true,
      transfert_source_seq: res.mode === 'de' ? res.noSeq : '',
      transfert_source_reg: res.mode === 'de' ? res.reg : '',
      transfert_source_bur: res.mode === 'de' ? res.bur : '',
      transfert_cible_reg: res.mode === 'vers' ? res.reg : '',
      transfert_cible_bur: res.mode === 'vers' ? res.bur : '',
      notes_non_imprimees: note.slice(0, 100),
    }));
    setIsDirty(true);
    setShowTransfertModal(false);
    showPopup("Transfert enregistré — n'oubliez pas d'enregistrer le dossier.", "info", "Transfert");
  };

  // Mode Entrevue : le questionnaire oriente l'entrevue mais ne crée PAS de DAJ.
  // On évalue la sélection (sans persister tQuestionnaire faute de noseqdaj),
  // puis on crée une entrevue FIR préremplie et on l'ouvre. La DAJ sera créée
  // plus tard depuis l'entrevue (bouton « Créer DAJ »), ce qui persistera alors
  // le questionnaire stocké et fera le lien DAJ ↔ entrevue.
  const handleQuestionnaireNon = async (reponses) => {
    const pending = pendingNouveau;
    setPendingNouveau(null);
    if (!pending) return;
    if (modeDemande === 'reguliere') {
      await creerDemandeReguliere(pending, reponses);
    } else {
      await creerEntrevue(pending, reponses);
    }
  };

  // Mode Régulière : le questionnaire de vérification est posé AVANT la création,
  // puis on crée directement la demande (DAJ) — SANS démarrer d'entrevue. Comme
  // la DAJ fournit un noseqdaj, le questionnaire est persisté dans tQuestionnaire.
  const creerDemandeReguliere = async (pending, reponses, opts = {}) => {
    const result = pending.type === 'existing'
      ? await initializeDossierForExistingClient(pending.client)
      : await initializeNewDossier(nouveauForm.nom, nouveauForm.prenom, nouveauForm.date_naissance, nouveauForm.nas);
    if (!result) return;
    setSansEntrevue(true);
    setIsDirty(true);
    if (reponses && result.no_sequence) {
      try {
        const { data } = await axios.post(`${API}/questionnaire`, {
          no_reg: defaultRegion, no_bur: currentBureau || defaultBureau,
          no_sequence: result.no_sequence,
          noclient: pending.client?.no_client || pending.client?.id || result.noclient || '',
          ...reponses,
        });
        if (data?.demande_docs) {
          showPopup("LE CLIENT(E) A ÉTÉ SÉLECTIONNÉ(E) !\n\nIl devra fournir la documentation nécessaire.", 'warning', 'Client sélectionné');
        }
      } catch (e) {
        console.error('Erreur enregistrement questionnaire (régulière):', e);
      }
    } else if (opts.mineur) {
      showPopup("CLIENT(E) DE MOINS DE 18 ANS.\n\nAucun questionnaire de vérification n'est requis.", 'info', 'Client de moins de 18 ans');
    }
  };

  // Crée une entrevue FIR préremplie et l'ouvre. Si `reponses` est null (client
  // mineur), on saute l'évaluation du questionnaire de vérification.
  const creerEntrevue = async (pending, reponses, opts = {}) => {
    try {
      let dec = { demande_docs: false };
      if (reponses) {
        const { data } = await axios.post(`${API}/questionnaire/evaluer`, {
          no_reg: defaultRegion, no_bur: currentBureau || defaultBureau, ...reponses,
        });
        dec = data;
      }
      const prefill = buildFirPrefill(pending);
      const nom = formatNomPropre(nouveauForm.nom.trim());
      const prenom = formatNomPropre(nouveauForm.prenom.trim());
      const { data: fir } = await axios.post(`${API}/fir`, {
        titre: `Entrevue ${prenom} ${nom}`.trim(),
        client_nom: nom, client_prenom: prenom, client_date_naissance: nouveauForm.date_naissance,
        answers: prefill,
        noclient: pending.client?.no_client || pending.client?.id || null,
        selectionne: !!dec.demande_docs,
        questionnaire: reponses ? { ...reponses, ...dec } : { mineur: true },
      });
      if (opts.mineur) {
        showPopup("CLIENT(E) DE MOINS DE 18 ANS.\n\nAucun questionnaire de vérification n'est requis.\nL'entrevue débute directement.", 'info', 'Client de moins de 18 ans');
      } else if (dec.demande_docs) {
        showPopup("LE CLIENT(E) A ÉTÉ SÉLECTIONNÉ(E) !\n\nIl devra fournir la documentation nécessaire.\nL'entrevue est marquée « Sélectionné — documentation ».", 'warning', 'Client sélectionné');
      }
      setFirInitialId(fir.id);
      setShowFir(true);
      loadStats();
    } catch (e) {
      showPopup(`Erreur lors de la création de l'entrevue :\n${e.response?.data?.detail || e.message}`, 'error', 'Entrevue');
    }
  };

  // Crée immédiatement une demande pour un client EXISTANT (le questionnaire
  // exige le numéro de séquence → noseqdaj dans tQuestionnaire).
  const initializeDossierForExistingClient = async (client) => {
    try {
      const response = await axios.post(`${API}/save-dossier`, {
        nom: client.nom, prenom: client.prenom, date_naissance: client.date_naissance,
        nas: client.nas || null, sexe: parseInt(client.sexe || 1), langue: String(client.langue || '1'),
        region: defaultRegion, bureau: currentBureau || defaultBureau,
        date_demande: todayLocal(),
        noclient: client.no_client || client.id, no_sequence: null,
        categorie_requerant: client.categorie_requerant || '01', nb_enfants: client.nb_enfants || 0,
        region_eloignee: bureauEstEloigne || !!client.region_eloignee, revision: false, reciprocite: false, transfert: false,
        transfert_source_seq: '', transfert_source_reg: '', transfert_source_bur: '', transfert_cible_reg: '', transfert_cible_bur: '', reciprocite_province: '', revision_note: '',
        adresse: client.adresse || '', ville: client.ville || '', code_postal: client.code_postal || '',
        province: client.province || 'QC',
        revenus: getEmptyFormData().revenus, actifs: getEmptyFormData().actifs
      });
      if (!response.data.success) return null;
      const result = response.data;
      setFormData(prev => ({
        ...getEmptyFormData(), no_dossier: '', no_reference: '', region: defaultRegion, bureau: currentBureau || defaultBureau,
        no_sequence: result.no_sequence, noclient: result.noclient, adda235: 0,
        date_demande: todayLocal(),
        nom: client.nom || '', prenom: client.prenom || '', date_naissance: client.date_naissance || '',
        sexe: String(client.sexe || 1), langue: String(client.langue || '1'), nas: client.nas || '',
        adresse: client.adresse || '', ville: client.ville || '', code_postal: client.code_postal || '',
        province: client.province || 'QC', telephone_residence: formatTelephone(client.telephone_residence || ''),
        telephone_bureau: formatTelephone(client.telephone_bureau || ''), cellulaire: formatTelephone(client.cellulaire || ''),
        courriel: client.courriel || '', categorie_requerant: client.categorie_requerant || '01',
        nb_enfants: client.nb_enfants || 0,
        region_eloignee: bureauEstEloigne || !!client.region_eloignee,
      }));
      setCurrentClient(client); setCurrentDossier({ no_seq_daj: result.no_sequence });
      setCurrentTab('requerant'); setEditMode('edition'); setIsDirty(false);
      showPopup(`Demande créée pour le client existant ${client.prenom} ${client.nom}.\nNo séquence: ${result.no_sequence}`, 'success', 'Nouvelle demande');
      loadStats();
      return { no_sequence: result.no_sequence, noclient: result.noclient };
    } catch (error) {
      showPopup(`Erreur lors de la création de la demande:\n${error.response?.data?.detail || error.message}`, 'error', 'Erreur');
      return null;
    }
  };

  const initializeNewDossier = async (nom, prenom, dateNaissance, nas) => {
    const clean = getEmptyFormData();
    const newData = {
      ...clean,
      nom: formatNomPropre(nom), prenom: formatNomPropre(prenom), date_naissance: dateNaissance,
      nas: formatNAS(nas)
    };
    try {
      const response = await axios.post(`${API}/save-dossier`, {
        nom: newData.nom, prenom: newData.prenom, date_naissance: newData.date_naissance,
        nas: newData.nas || null, sexe: parseInt(newData.sexe), langue: newData.langue,
        region: newData.region || defaultRegion, bureau: newData.bureau || defaultBureau,
        date_demande: newData.date_demande, noclient: null, no_sequence: null,
        categorie_requerant: newData.categorie_requerant, nb_enfants: 0,
        region_eloignee: bureauEstEloigne, revision: false, reciprocite: false, transfert: false,
        transfert_source_seq: '', transfert_source_reg: '', transfert_source_bur: '', transfert_cible_reg: '', transfert_cible_bur: '', reciprocite_province: '', revision_note: '',
        revenus: newData.revenus, actifs: newData.actifs
      });
      if (response.data.success) {
        const result = response.data;
        setFormData({ ...newData, region_eloignee: bureauEstEloigne, no_sequence: result.no_sequence, noclient: result.noclient });
        setCurrentClient({ id: result.noclient, no_client: result.noclient, nom: newData.nom, prenom: newData.prenom });
        setCurrentDossier({ no_seq_daj: result.no_sequence });
        setCurrentTab('requerant'); setEditMode('edition'); setIsDirty(false);
        showPopup(`Nouveau dossier créé et enregistré.\nNo client: ${result.noclient}\nNo séquence: ${result.no_sequence}`, 'success', 'Nouveau dossier');
        loadStats();
        return { no_sequence: result.no_sequence, noclient: result.noclient };
      }
      return null;
    } catch (error) {
      console.error('Erreur création dossier:', error);
      showPopup(`Erreur lors de la création du dossier:\n${error.response?.data?.detail || error.message}`, 'error', 'Erreur');
      return null;
    }
  };

  const handleRechercher = async () => {
    checkDirtyThen(() => {
      fermerDossierCourant();     // vider le dossier en cours après validation save/annuler
      setShowRecherche(true);
    });
  };

  // Lorsqu'une ligne de RechercheModal est sélectionnée (double-clic ou Entrée),
  // on charge le client correspondant puis on ouvre le dossier DAJ identifié par no_seq_daj.
  const handleSelectRechercheRow = async (row) => {
    setShowRecherche(false);
    if (!row || !row.no_client) {
      showPopup("Cet enregistrement n'a pas de client associé.", "error", "Recherche");
      return;
    }
    try {
      const clientRes = await axios.get(`${API}/clients/${row.no_client}`);
      // Bug fix (v22): on injecte no_seq_daj/pk_daj dans l'objet client passé à
      // handleSelectClient afin que la sélection du DAJ à charger cible bien
      // le dossier choisi dans la Recherche (et non le dernier du tableau).
      await handleSelectClient({
        ...clientRes.data,
        no_seq_daj: row.no_seq_daj,
        pk_daj: row.pk_daj,
      });
      // Date de dernière modification du dossier (addadatestatut)
      setDossierDateModif(row.date_modification || null);
      // ÉTAPE 1 — Toujours appliquer les champs de la ligne sélectionnée (header).
      // Ces valeurs sont disponibles immédiatement et ne dépendent pas d'un appel
      // supplémentaire à /api/daj (qui pourrait échouer ou retourner un autre DAJ).
      setFormData(prev => ({
        ...prev,
        no_sequence: row.no_seq_daj || prev.no_sequence,
        no_reference: row.no_ref || prev.no_reference,
        no_dossier: row.no_dossier || prev.no_dossier,
        bureau: row.no_bur || prev.bureau,
        date_demande: row.date_demande || prev.date_demande,
      }));
      // ÉTAPE 2 — Bonus : si on peut charger le DAJ complet et qu'il correspond,
      // on récupère les détails Revenus/Décision/Service. En cas d'échec, l'étape 1
      // garantit que l'en-tête reste correct.
      try {
        const dajList = await axios.get(`${API}/daj?client_id=${row.no_client}`);
        const allDaj = dajList.data || [];
        const target = allDaj.find(d => (d.no_seq_daj || d.id) === row.no_seq_daj);
        if (target) {
          setCurrentDossier(target);
          setFormData(prev => ({
            ...prev,
            no_sequence: row.no_seq_daj || target.no_seq_daj || prev.no_sequence,
            no_reference: row.no_ref || target.no_ref || prev.no_reference,
            no_dossier: row.no_dossier || target.no_dossier || prev.no_dossier,
            date_demande: row.date_demande || target.date_demande || prev.date_demande,
            // Fields propres à la DAJ (peuvent différer du client) — parité legacy :
            // adda208 = Sexe, adda209 = Langue, adda233 = Région éloignée.
            // Sans ce merge, l'ÉTAPE 1 avait laissé les valeurs du client OU
            // d'un DAJ antérieur, causant l'affichage de "Français" au lieu
            // de "En Anglais" pour le DAJ 226174007-01.
            langue: (target.langue !== null && target.langue !== undefined && target.langue !== '')
                      ? String(target.langue) : prev.langue,
            sexe: (target.sexe !== null && target.sexe !== undefined && target.sexe !== '')
                    ? String(target.sexe) : prev.sexe,
            region_eloignee: (target.region_eloignee !== undefined && target.region_eloignee !== null)
                               ? !!target.region_eloignee : prev.region_eloignee,
            // Transfert (FrmTrans legacy — adda234 + source/cible)
            transfert: !!target.transfert,
            transfert_source_seq: target.transfert_source_seq || '',
            transfert_source_reg: target.transfert_source_reg || '',
            transfert_source_bur: target.transfert_source_bur || '',
            transfert_cible_reg: target.transfert_cible_reg || '',
            transfert_cible_bur: target.transfert_cible_bur || '',
            // Réciprocité (adda230 + adda514)
            reciprocite: !!target.reciprocite,
            reciprocite_province: target.reciprocite_province || '',
            // Révision (adda228 + adda680)
            revision: !!target.revision,
            revision_note: target.revision_note || '',
            revenus: { ...prev.revenus, ...(target.revenus || {}) },
            decision: target.decision || prev.decision,
            service: target.service ? { ...prev.service, ...target.service } : prev.service,
            // Champs read-only de l'onglet Statut (proviennent du DAJ ciblé) —
            // sans ce merge, « Dossier fermé » restait à Non même si addaferme > 0.
            dossier_ferme: !!target.dossier_ferme,
            date_fermeture_statut: target.date_fermeture_statut || '',
            date_statut: target.date_statut || '',
            date_depuis: target.date_depuis || '',
            date_cheminement: target.date_cheminement || '',
            date_signature_declarant: target.date_signature_declarant || '',
            date_reception_carte: target.date_reception_carte || '',
            info_manquante: target.info_manquante ?? prev.info_manquante,
            // Merge art_6_ou_7 et interets_opposes depuis le DAJ ciblé (adda248).
            // Le reste du conjoint (nom, adresse, etc.) provient déjà de clliens
            // via handleSelectClient. Voir aussi /app/backend/server.py adda248.
            conjoint: prev.conjoint ? {
              ...prev.conjoint,
              art_6_ou_7: (target.conjoint && target.conjoint.art_6_ou_7) || prev.conjoint.art_6_ou_7 || '',
              interets_opposes: target.conjoint ? !!target.conjoint.interets_opposes : !!prev.conjoint.interets_opposes,
            } : prev.conjoint,
          }));
          // Hydrate l'établissement (legacy : daj.spair2 contient l'id_etablis).
          // On va chercher le détail dans la table referencielle pour afficher
          // le nom à côté du bouton Établissement de l'onglet Requérant.
          if (target.id_etablis) {
            try {
              const etabRes = await axios.get(`${API}/reference/etablissements`, {
                params: { include_inactive: true },
              });
              const found = (etabRes.data || []).find(
                e => e.id_etablis === target.id_etablis
              );
              if (found) setEtablissementSelectionne(found);
            } catch (e) {
              console.warn("Hydratation établissement échouée :", e);
            }
          } else {
            setEtablissementSelectionne(null);
          }
        }
      } catch (err) {
        console.warn("Impossible de charger le DAJ détaillé :", err);
      }
      // ÉTAPE 3 — Fallback dédié : charger les revenus via l'endpoint qui lit
      // directement les colonnes adda3XX du dossier (fiable sur SQLite + SQL Server).
      if (row.pk_daj) {
        try {
          const revRes = await axios.get(`${API}/daj/${row.pk_daj}/revenus`);
          const r = revRes.data || {};
          setFormData(prev => ({
            ...prev,
            revenus: {
              ...prev.revenus,
              emploi_req: r.adda300 ?? prev.revenus.emploi_req,
              assurance_emploi_req: r.adda302 ?? prev.revenus.assurance_emploi_req,
              securite_revenu_req: r.adda304 ?? prev.revenus.securite_revenu_req,
              csst_req: r.adda306 ?? prev.revenus.csst_req,
              saaq_req: r.adda308 ?? prev.revenus.saaq_req,
              pension_vieillesse_req: r.adda310 ?? prev.revenus.pension_vieillesse_req,
              pension_alimentaire_req: r.adda312 ?? prev.revenus.pension_alimentaire_req,
              pension_alimentaire_enfants_req: r.adda313 ?? prev.revenus.pension_alimentaire_enfants_req,
              rrq_rpc_req: r.adda314 ?? prev.revenus.rrq_rpc_req,
              autres_revenus_req: r.adda316 ?? prev.revenus.autres_revenus_req,
              emploi_fam: r.adda320 ?? prev.revenus.emploi_fam,
              assurance_emploi_fam: r.adda322 ?? prev.revenus.assurance_emploi_fam,
              securite_revenu_fam: r.adda324 ?? prev.revenus.securite_revenu_fam,
              csst_fam: r.adda326 ?? prev.revenus.csst_fam,
              saaq_fam: r.adda328 ?? prev.revenus.saaq_fam,
              pension_vieillesse_fam: r.adda330 ?? prev.revenus.pension_vieillesse_fam,
              pension_alimentaire_fam: r.adda332 ?? prev.revenus.pension_alimentaire_fam,
              pension_alimentaire_enfants_fam: r.adda333 ?? prev.revenus.pension_alimentaire_enfants_fam,
              rrq_rpc_fam: r.adda334 ?? prev.revenus.rrq_rpc_fam,
              autres_revenus_fam: r.adda336 ?? prev.revenus.autres_revenus_fam,
              famille_incluse: !!r.adda339,
              deficience: r.adda344 ?? prev.revenus.deficience,
              frais_garde: r.adda346 ?? prev.revenus.frais_garde,
              pension_alim_versee: r.adda348 ?? prev.revenus.pension_alim_versee,
              frais_scolaires: r.adda350 ?? prev.revenus.frais_scolaires,
              autres_deductions: r.adda352 ?? prev.revenus.autres_deductions,
            },
            notes_non_imprimees: r.notes_non_imprimees ?? prev.notes_non_imprimees ?? '',
          }));
        } catch (err) {
          console.warn("Endpoint /daj/{pk_daj}/revenus indisponible :", err);
        }
      }
      setMessage(`Dossier ${row.no_seq_daj || ''} (${row.nom_requerant || ''}) chargé`);
    } catch (e) {
      console.error("Erreur chargement client après recherche:", e);
      showPopup(`Impossible de charger le client : ${e.response?.data?.detail || e.message}`, "error", "Recherche");
    }
  };

  const handleSelectClient = async (client) => {
    // Reset complet du formulaire avant de charger le nouveau client
    const clean = getEmptyFormData();
    const clientId = client.no_client || client.noclient || client.id;
    setCurrentClient(client);
    setCurrentDossier(null);
    setFormData({
      ...clean,
      nom: client.nom, prenom: client.prenom, date_naissance: client.date_naissance || '',
      sexe: String(client.sexe), langue: client.langue, nas: client.nas || '',
      adresse: client.adresse || '', ville: client.ville || '', code_postal: client.code_postal || '',
      province: client.province || 'QC', telephone_residence: client.telephone_residence || '',
      telephone_bureau: client.telephone_bureau || '', cellulaire: client.cellulaire || '',
      courriel: client.courriel || '', categorie_requerant: client.categorie_requerant || '01',
      nb_enfants: client.nb_enfants || 0, region_eloignee: client.region_eloignee || false,
      noclient: clientId, occupation: client.occupation || ''
    });
    setIsSearching(false); setEditMode('edition'); setIsDirty(false);
    setCurrentTab('requerant');
    // Reset l'établissement — il sera ré-hydraté plus tard si le dossier en a un
    setEtablissementSelectionne(null);
    setMessage(`Client ${client.nom}, ${client.prenom} chargé`);
    
    // Charger les liens du client + extraire le conjoint
    try {
      const liensRes = await axios.get(`${API}/clients/${clientId}/liens`);
      const allLiens = liensRes.data || [];
      const conjointRow = allLiens.find(l => isConjointType(l.type_lien));
      const liensSansConjoint = allLiens.filter(l => !isConjointType(l.type_lien));
      const mapLien = (d) => d.nom ? { type_lien: d.type_lien || '', nom: d.nom || '', prenom: d.prenom || '', date_naissance: d.date_naissance || '', sexe: String(d.sexe || ''), nas: d.nas || '', interets_opposes: d.interets_opposes || false, adresse: d.adresse || '', ville: d.ville || '', province: d.province || 'QC', code_postal: d.code_postal || '', telephone_residence: d.telephone_residence || '', telephone_bureau: d.telephone_bureau || '', telephone_ext: d.telephone_ext || '', courriel: d.courriel || '' } : { ...emptyLien };
      // Mapper le conjoint vers formData.conjoint (mêmes champs que le formulaire ConjointTab)
      const mapConjoint = (c) => ({
        nom: c.nom || '',
        prenom: c.prenom || '',
        date_naissance: c.date_naissance || '',
        sexe: String(c.sexe || ''),
        nas: c.nas || '',
        art_6_ou_7: c.art_6_ou_7 || '',
        interets_opposes: c.interets_opposes || false,
        adresse: c.adresse || '',
        ville: c.ville || '',
        province: c.province || 'QC',
        code_postal: c.code_postal || '',
        telephone_residence: c.telephone_residence || '',
        telephone_bureau: c.telephone_bureau || '',
        telephone_ext: c.telephone_ext || '',
        courriel: c.courriel || ''
      });
      setFormData(prev => ({
        ...prev,
        conjoint: conjointRow ? mapConjoint(conjointRow) : prev.conjoint,
        lien1: mapLien(liensSansConjoint[0] || {}),
        lien2: mapLien(liensSansConjoint[1] || {})
      }));
      setLiensListe(liensSansConjoint);
    } catch (e) { console.error('Erreur chargement liens:', e); }
    
    // Charger le DAJ sélectionné (par no_seq_daj/pk_daj si fourni depuis la
    // recherche) ou le plus récent du client à défaut.
    // ⚠ Bug fix (v21) : avant, on prenait `res.data[res.data.length - 1]`
    // sans filtrer → chargement du mauvais dossier (avec ses champs langue,
    // sexe, service…) même quand l'utilisateur en avait sélectionné un
    // spécifique. Fix : chercher par no_seq_daj/pk_daj/no_sequence si dispo.
    try {
      const res = await axios.get(`${API}/daj?client_id=${clientId}`);
      if (res.data && res.data.length > 0) {
        const targetSeq = (client && (client.no_seq_daj || client.pk_daj || client.no_sequence)) || '';
        const matched = targetSeq
          ? res.data.find(d => d.no_seq_daj === targetSeq || d.id === targetSeq)
          : null;
        const daj = matched || res.data[res.data.length - 1];
        setCurrentDossier(daj);
        // Parité legacy `CodeDaj.vb:RempFin` — le snapshot requérant stocké
        // dans daj.adda210-218 + adda501 prime sur les valeurs actuelles de
        // `client` chargées par handleSelectClient. Cela préserve les infos
        // du requérant AU MOMENT DE LA DEMANDE (indépendamment des
        // changements ultérieurs de client/cladress).
        const snap = daj.snapshot_requerant || {};
        setFormData(prev => ({
          ...prev,
          no_sequence: daj.no_seq_daj || daj.id || '',
          no_reference: daj.no_ref || daj.no_reference || prev.no_reference,
          no_dossier: daj.no_dossier || prev.no_dossier,
          adda235: daj.adda235 ?? 0,
          date_demande: daj.date_demande || prev.date_demande,
          // Snapshot requérant : priorité daj > client (parité legacy)
          adresse: snap.adresse || prev.adresse,
          ville: snap.ville || prev.ville,
          province: snap.province || prev.province,
          code_postal: snap.code_postal || prev.code_postal,
          telephone_residence: snap.telephone_residence || prev.telephone_residence,
          cellulaire: snap.cellulaire || prev.cellulaire,
          categorie_requerant: snap.categorie_requerant || prev.categorie_requerant,
          // Langue (parité legacy) : la DAJ possède sa propre langue (adda208)
          // qui peut différer de la langue par défaut du client (clcllangue).
          // Si le dossier existant a une langue renseignée → elle prime sur
          // celle du client déjà chargée par handleSelectClient.
          langue: (daj.langue !== null && daj.langue !== undefined && daj.langue !== '')
                    ? String(daj.langue) : prev.langue,
          revenus: { ...clean.revenus, ...daj.revenus },
          decision: daj.decision || prev.decision,
          service: daj.service ? { ...clean.service, ...daj.service } : prev.service,
          // Conjoint : prendre depuis daj.adda240-258 (legacy) si le conjoint n'a pas
          // été trouvé dans clliens. Le legacy VB.NET stocke le conjoint dans daj,
          // PAS dans clliens.
          // IMPORTANT: art_6_ou_7 et interets_opposes sont stockés UNIQUEMENT
          // dans daj.adda248 — clliens ne les expose pas. On les merge donc
          // TOUJOURS depuis daj.conjoint, même quand le reste du conjoint vient
          // de clliens (sinon l'article 7 ne se recharge jamais après save).
          conjoint: (prev.conjoint && prev.conjoint.nom)
            ? {
                ...prev.conjoint,
                art_6_ou_7: (daj.conjoint && daj.conjoint.art_6_ou_7) || prev.conjoint.art_6_ou_7 || '',
                interets_opposes: daj.conjoint ? !!daj.conjoint.interets_opposes : !!prev.conjoint.interets_opposes,
              }
            : (daj.conjoint ? {
                nom: daj.conjoint.nom || '',
                prenom: daj.conjoint.prenom || '',
                date_naissance: daj.conjoint.date_naissance || '',
                sexe: String(daj.conjoint.sexe ?? ''),
                nas: daj.conjoint.nas || '',
                art_6_ou_7: daj.conjoint.art_6_ou_7 || '',
                interets_opposes: !!daj.conjoint.interets_opposes,
                adresse: daj.conjoint.adresse || '',
                ville: daj.conjoint.ville || '',
                province: daj.conjoint.province || 'QC',
                code_postal: daj.conjoint.code_postal || '',
                telephone_residence: daj.conjoint.telephone_residence || '',
                telephone_bureau: daj.conjoint.telephone_bureau || '',
                telephone_ext: daj.conjoint.telephone_ext || '',
                courriel: daj.conjoint.courriel || ''
              } : prev.conjoint)
        }));
      }
    } catch (e) { console.error('Erreur chargement DAJ:', e); }
  };

  // Navigation entre les images/demandes du requérant (parité legacy
  // FctLoadPrecedentSuivant). Parcourt TOUTES les demandes du client (images
  // 01, 02… ; exclut l'image maître '00'), triées par no_seq_daj croissant.
  // Ordre INTUITIF : Début = plus ancienne, Fin = plus récente.
  const navigateImage = async (direction) => {
    const clientId = currentClient?.no_client || currentClient?.noclient
      || currentClient?.id || formData.noclient;
    if (!clientId) return;
    try {
      const res = await axios.get(`${API}/daj?client_id=${clientId}`);
      const list = (res.data || [])
        .map(d => ({ ...d, _seq: d.no_seq_daj || d.id || '' }))
        .filter(d => d._seq && d._seq.slice(-2) !== '00')
        .sort((a, b) => a._seq.localeCompare(b._seq));
      if (list.length <= 1) {
        setMessage("Aucune autre demande à afficher pour ce requérant.");
        return;
      }
      const curSeq = formData.no_sequence || currentDossier?.no_seq_daj || '';
      let idx = list.findIndex(d => d._seq === curSeq);
      if (idx < 0) idx = list.length - 1;
      let target = idx;
      if (direction === 'debut') target = 0;
      else if (direction === 'fin') target = list.length - 1;
      else if (direction === 'precedent') target = Math.max(0, idx - 1);
      else if (direction === 'suivant') target = Math.min(list.length - 1, idx + 1);
      if (target === idx) return; // déjà à l'extrémité
      const targetSeq = list[target]._seq;
      await handleSelectClient({ ...currentClient, no_client: clientId, no_seq_daj: targetSeq });
      setMessage(`Demande ${target + 1} de ${list.length} — ${targetSeq}`);
    } catch (e) {
      setMessage(`Erreur navigation : ${e?.response?.data?.detail || e.message}`);
    }
  };


  // + EnrDaj(True)) : duplique la demande ouverte en une NOUVELLE demande pour le
  // même requérant (nouvelle séquence, statut/cheminement remis à zéro).
  const handleEnregistrerSous = async () => {
    const noSeq = currentDossier?.no_seq_daj || formData.no_sequence || '';
    if (!noSeq) {
      showPopup("« Enregistrer sous » nécessite une demande existante ouverte.", 'warning', 'Enregistrer sous');
      return;
    }
    const ok = window.confirm(
      "ATTENTION\nUtiliser dans le seul cas où nous devons ouvrir plusieurs demandes pour le même requérant au même moment.\n\nVoulez-vous Enregistrer sous?"
    );
    if (!ok) return;
    await handleEnregistrer({ enregistrerSous: true });
  };

  // Envoi du dossier/requérant vers JurisÉvolution (création ou mise à jour).
  const handleSyncJurisEvolution = async () => {
    const noSeq = currentDossier?.no_seq_daj || formData.no_sequence || '';
    if (!noSeq) {
      showPopup("Veuillez d'abord ouvrir un dossier.", 'warning', 'JurisÉvolution');
      return;
    }
    setJeSyncing(true);
    setJeSyncPhase('validation');
    try {
      // 1) Prévisualisation : le dossier existe-t-il déjà dans JurisÉvolution ?
      const { data } = await axios.get(`${API}/jurisevolution/preview/${encodeURIComponent(noSeq)}`);
      if (data?.success === false) {
        const type = (data?.code === 'not_configured' || data?.code === 'validation') ? 'warning' : 'error';
        showPopup(data?.message || 'Erreur JurisÉvolution.', type, 'JurisÉvolution', data?.details || '');
        setJeSyncing(false);
        setJeSyncPhase('');
        return;
      }
      if (data?.exists) {
        // Dossier existant → confirmation avec comparaison avant d'écraser.
        setJeSyncing(false);
        setJeSyncPhase('');
        setJurisConfirm({ open: true, noSeq, matterId: data.matter_id, fields: data.fields || [], currentUnavailable: !!data.current_unavailable });
        return;
      }
      // Nouveau dossier → création directe.
      await doSyncJuris(noSeq);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Erreur de communication avec JurisÉvolution.';
      showPopup(msg, 'error', 'JurisÉvolution', e?.response?.data?.details || '');
      setJeSyncing(false);
      setJeSyncPhase('');
    }
  };

  const doSyncJuris = async (noSeq) => {
    setJeSyncing(true);
    setJeSyncPhase('sync');
    try {
      const { data } = await axios.post(`${API}/jurisevolution/sync/${encodeURIComponent(noSeq)}`);
      if (data?.success) {
        showPopup(data.message || 'Dossier synchronisé avec JurisÉvolution.', 'success', 'JurisÉvolution');
      } else {
        const type = (data?.code === 'already_synced') ? 'info'
          : (data?.code === 'not_configured' || data?.code === 'validation') ? 'warning' : 'error';
        showPopup(data?.message || 'Échec de la synchronisation JurisÉvolution.', type, 'JurisÉvolution', data?.details || '');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Erreur de communication avec JurisÉvolution.';
      showPopup(msg, 'error', 'JurisÉvolution', e?.response?.data?.details || '');
    } finally {
      setJeSyncing(false);
      setJeSyncPhase('');
    }
  };


  const handleEnregistrer = async (opts = {}) => {
    const sous = opts.enregistrerSous === true;
    if (!formData.nom || !formData.prenom || !formData.date_naissance) { showPopup('Le nom, le prénom et la date de naissance sont obligatoires.', 'error', 'Champs obligatoires'); return; }
    if (formData.region === '18' && !formData.no_dossier?.trim()) { showPopup('Le numéro de dossier est obligatoire pour la région 18 (Montréal).', 'error', 'Champ obligatoire'); return; }
    const today = todayLocal();
    if (formData.date_demande > today) { showPopup('La date de demande ne peut pas être supérieure à la date du jour.', 'error', 'Date invalide'); return; }
    if (formData.nas && formData.nas.length === 11) { const nasResult = validateNAS(formData.nas); if (!nasResult.valid) { showPopup(nasResult.message, 'error', 'N.A.S. invalide'); return; } }

    let payload = null;
    try {
      payload = {
        enregistrer_sous: sous,
        no_dossier: formData.no_dossier || null, no_reference: sous ? null : (formData.no_reference || null),
        no_sequence: sous ? null : (currentDossier?.no_seq_daj || formData.no_sequence || null),
        region: formData.region || defaultRegion, bureau: formData.bureau || defaultBureau, date_demande: formData.date_demande,
        noclient: currentClient?.id || currentClient?.no_client || null,
        nom: formData.nom, prenom: formData.prenom, date_naissance: formData.date_naissance,
        sexe: parseInt(formData.sexe), langue: formData.langue, nas: formData.nas || null,
        no_bande: formData.no_bande || null, sed: formData.sed || null,
        categorie_requerant: formData.categorie_requerant, nb_enfants: formData.nb_enfants || 0,
        region_eloignee: sous ? false : (formData.region_eloignee || false),
        adresse: formData.adresse || null, ville: formData.ville || null, province: formData.province || 'QC',
        code_postal: formData.code_postal || null, telephone_residence: formData.telephone_residence || null,
        telephone_bureau: formData.telephone_bureau || null, telephone_ext: formData.telephone_ext || null,
        cellulaire: formData.cellulaire || null, courriel: formData.courriel || null,
        revision: formData.revision || false, reciprocite: formData.reciprocite || false,
        revision_note: formData.revision_note || null,
        reciprocite_province: formData.reciprocite_province || null,
        transfert: formData.transfert || false, no_aide_sociale: formData.no_aide_sociale || null,
        transfert_source_seq: formData.transfert_source_seq || null,
        transfert_source_reg: formData.transfert_source_reg || null,
        transfert_source_bur: formData.transfert_source_bur || null,
        transfert_cible_reg: formData.transfert_cible_reg || null,
        transfert_cible_bur: formData.transfert_cible_bur || null,
        montant_aide_sociale: formData.montant_aide_sociale || 0,
        conjoint: formData.conjoint?.nom ? { nom: formData.conjoint.nom, prenom: formData.conjoint.prenom || null, date_naissance: formData.conjoint.date_naissance || null, sexe: formData.conjoint.sexe ? parseInt(formData.conjoint.sexe) : null, nas: formData.conjoint.nas || null, art_6_ou_7: formData.conjoint.art_6_ou_7 || null, interets_opposes: formData.conjoint.interets_opposes || false, adresse: formData.conjoint.adresse || null, ville: formData.conjoint.ville || null, province: formData.conjoint.province || 'QC', code_postal: formData.conjoint.code_postal || null, telephone_residence: formData.conjoint.telephone_residence || null, telephone_bureau: formData.conjoint.telephone_bureau || null, telephone_ext: formData.conjoint.telephone_ext || null, courriel: formData.conjoint.courriel || null } : null,
        lien1: formData.lien1?.type_lien ? { type_lien: formData.lien1.type_lien, nom: formData.lien1.nom || null, prenom: formData.lien1.prenom || null, date_naissance: formData.lien1.date_naissance || null, sexe: formData.lien1.sexe ? parseInt(formData.lien1.sexe) : null, nas: formData.lien1.nas || null, interets_opposes: formData.lien1.interets_opposes || false, adresse: formData.lien1.adresse || null, ville: formData.lien1.ville || null, province: formData.lien1.province || 'QC', code_postal: formData.lien1.code_postal || null, telephone_residence: formData.lien1.telephone_residence || null, telephone_bureau: formData.lien1.telephone_bureau || null, telephone_ext: formData.lien1.telephone_ext || null, courriel: formData.lien1.courriel || null } : null,
        lien2: formData.lien2?.type_lien ? { type_lien: formData.lien2.type_lien, nom: formData.lien2.nom || null, prenom: formData.lien2.prenom || null, date_naissance: formData.lien2.date_naissance || null, sexe: formData.lien2.sexe ? parseInt(formData.lien2.sexe) : null, nas: formData.lien2.nas || null, interets_opposes: formData.lien2.interets_opposes || false, adresse: formData.lien2.adresse || null, ville: formData.lien2.ville || null, province: formData.lien2.province || 'QC', code_postal: formData.lien2.code_postal || null, telephone_residence: formData.lien2.telephone_residence || null, telephone_bureau: formData.lien2.telephone_bureau || null, telephone_ext: formData.lien2.telephone_ext || null, courriel: formData.lien2.courriel || null } : null,
        revenus: formData.revenus, actifs: formData.actifs, notes: formData.notes || null,
        notes_non_imprimees: formData.notes_non_imprimees || null,
        service: sous ? { ...(formData.service || {}), no_dossier_cour: '' } : (formData.service || null),
        // Décision (toutes les checkboxes + signature avocat)
        decision: formData.decision || null,
        // Établissement (legacy : id_etablis sera stocké dans daj.spair2)
        id_etablis: etablissementSelectionne?.id_etablis || null
      };
      const response = await axios.post(`${API}/save-dossier`, payload);

      if (response.data.success) {
        const result = response.data;
        setCurrentClient({ id: result.noclient, no_client: result.noclient, nom: formData.nom, prenom: formData.prenom });
        setFormData(prev => ({
          ...prev, no_sequence: result.no_sequence, noclient: result.noclient,
          decision: { ...(prev.decision || {}), ...(result.decision || {}) },
          ...(sous ? {
            adda235: 1,
            no_reference: '', region_eloignee: false,
            date_signature_declarant: '', info_manquante: '',
            service: { ...(prev.service || {}), no_dossier_cour: '' },
          } : {}),
        }));
        setCurrentDossier({ no_seq_daj: result.no_sequence });
        setDossierDateModif(result.date_modification || null);
        setEditMode('edition');
        setIsDirty(false);
        const dec = result.decision;
        if (dec) {
          let msgDetail = `${sous ? 'Nouvelle demande créée (Enregistrer sous)' : (result.is_new_client ? 'Nouveau dossier créé' : 'Dossier mis à jour')} avec succès.\n`;
          msgDetail += `No client: ${result.noclient}\nNo sequence: ${result.no_sequence}\n\nResultat: ${dec.message}`;
          if (dec.avec_contribution) msgDetail += `\nContribution maximale: ${dec.contribution_max}$`;
          showPopup(msgDetail, dec.admissible ? 'success' : 'warning', 'Enregistrement');
        } else { showPopup(result.message, 'success', 'Enregistrement'); }
      }
      loadStats();
      return response.data.success === true;
    } catch (error) {
      // Cas spécial : changement critique → l'API demande confirmation pour
      // créer une nouvelle image (parité legacy ValidParamImage).
      const detail = error.response?.data?.detail;
      if (
        error.response?.status === 409 &&
        detail &&
        typeof detail === 'object' &&
        detail.code === 'NOUVELLE_IMAGE_REQUISE'
      ) {
        setRevisionModal({
          open: true,
          changements: detail.changements || [],
          image_actuelle: detail.image_actuelle || '',
          image_nouvelle: detail.image_nouvelle || '',
          note: '',
          pendingPayload: payload,
        });
        return;
      }
      // Garde-fou "dernière image" : message legacy tel quel (titre "Modifier", info).
      if (error.response?.status === 409 && typeof detail === 'string') {
        showPopup(detail, 'info', 'Modifier');
        return;
      }
      console.error('Erreur:', error);
      showPopup(`Erreur lors de l'enregistrement:\n${detail || error.message}`, 'error', 'Erreur');
    }
  };

  // Confirmation de la création d'une nouvelle image (parité legacy)
  const confirmerNouvelleImage = async () => {
    const m = revisionModal;
    if (!m.note?.trim()) {
      showPopup('Veuillez saisir une note de révision.', 'error', 'Note requise');
      return;
    }
    if (!m.pendingPayload) {
      setRevisionModal(prev => ({ ...prev, open: false }));
      return;
    }
    try {
      const payload2 = {
        ...m.pendingPayload,
        confirm_nouvelle_image: true,
        note_revision: m.note.trim(),
      };
      const response = await axios.post(`${API}/save-dossier`, payload2);
      if (response.data.success) {
        const result = response.data;
        setCurrentClient({ id: result.noclient, no_client: result.noclient, nom: formData.nom, prenom: formData.prenom });
        setFormData(prev => ({ ...prev, no_sequence: result.no_sequence, noclient: result.noclient, decision: { ...(prev.decision || {}), ...(result.decision || {}) } }));
        setCurrentDossier({ no_seq_daj: result.no_sequence });
        setDossierDateModif(result.date_modification || null);
        setEditMode('edition');
        setIsDirty(false);
        showPopup(
          `Nouvelle image créée : ${result.no_sequence}\nL'image précédente (${m.image_actuelle}) est désormais historique.`,
          'success',
          'Nouvelle image',
        );
      }
      setRevisionModal({ open: false, changements: [], image_actuelle: '', image_nouvelle: '', note: '', pendingPayload: null });
      loadStats();
    } catch (e) {
      showPopup(`Erreur création nouvelle image :\n${e.response?.data?.detail || e.message}`, 'error', 'Erreur');
    }
  };

  const handleCalculer = async () => {
    if (!currentClient) { setMessage('Veuillez d\'abord enregistrer le client'); return; }
    try {
      const response = await axios.post(`${API}/calcul-admissibilite?categorie=${formData.categorie_requerant}&date_demande=${formData.date_demande}&region_eloignee=${formData.region_eloignee}&reciprocite=${formData.reciprocite}`, { revenus: formData.revenus, actifs: formData.actifs });
      setFormData(prev => ({ ...prev, decision: response.data })); setCurrentTab('decision'); setMessage('Calcul d\'admissibilité effectué');
    } catch (error) { setMessage('Erreur lors du calcul'); }
  };

  const handleVerifierRefusPrecedent = async () => {
    const nom = (formData.nom || '').trim();
    const prenom = (formData.prenom || '').trim();
    if (!nom || !prenom) { setMessage('Veuillez saisir le nom et le prénom du requérant pour vérifier un refus précédent'); return; }
    setMessage('Vérification du refus précédent en cours...');
    try {
      const resp = await axios.get(`${API}/refus-precedent`, { params: { nom, prenom } });
      if (resp.data.count > 0) {
        setRefusPrecedentData({ nom, prenom, colonnes: resp.data.colonnes, resultats: resp.data.resultats });
        setShowRefusPrecedent(true);
        setMessage(`${resp.data.count} refus précédent(s) trouvé(s) pour ${prenom} ${nom}`);
      } else {
        showPopup(`Il n'y a pas de refus concernant ${prenom} ${nom}.`, 'info', 'Recherche de refus');
        setMessage(`Aucun refus précédent trouvé pour ${prenom} ${nom}`);
      }
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Erreur lors de la vérification du refus précédent');
    }
  };

  const handleCreerDossier = async () => {
    if (!currentClient) { setMessage('Veuillez d\'abord enregistrer le client'); return; }
    try {
      const response = await axios.post(`${API}/daj`, { client_id: currentClient.id, date_demande: formData.date_demande, nature_affaire: '', revenus: formData.revenus, actifs: formData.actifs, reciprocite: formData.reciprocite, notes: formData.notes });
      setCurrentDossier(response.data); setFormData(prev => ({ ...prev, no_sequence: response.data.no_seq_daj, decision: response.data.decision }));
      setCurrentTab('decision'); loadStats(); setMessage('Dossier DAJ créé avec succès');
    } catch (error) { setMessage('Erreur lors de la création du dossier'); }
  };

  // Calcul des totaux
  const totalRevenusReq = formData.revenus.emploi_req + formData.revenus.assurance_emploi_req + formData.revenus.securite_revenu_req + formData.revenus.csst_req + formData.revenus.saaq_req + formData.revenus.pension_vieillesse_req + formData.revenus.pension_alimentaire_req + formData.revenus.pension_alimentaire_enfants_req + formData.revenus.rrq_rpc_req + formData.revenus.autres_revenus_req;
  const totalRevenusFam = formData.revenus.emploi_fam + formData.revenus.assurance_emploi_fam + formData.revenus.securite_revenu_fam + formData.revenus.csst_fam + formData.revenus.saaq_fam + formData.revenus.pension_vieillesse_fam + formData.revenus.pension_alimentaire_fam + formData.revenus.pension_alimentaire_enfants_fam + formData.revenus.rrq_rpc_fam + formData.revenus.autres_revenus_fam;
  const totalDeductions = formData.revenus.deficience + formData.revenus.frais_garde + formData.revenus.pension_alimentaire_payee + (formData.nb_enfants * 6000) + formData.revenus.frais_scolaires;

  // Onglets
  const tabs = [
    { id: 'requerant', label: 'Requérant' }, { id: 'conjoint', label: 'Conjoint' },
    { id: 'liens', label: 'Liens' }, { id: 'revenus', label: 'Revenus' },
    { id: 'actifs', label: 'Actifs' }, { id: 'service', label: 'Service' },
    { id: 'decision', label: 'Décision' }, { id: 'statut', label: 'Statut' },
  ];
  const isDisabled = editMode === 'vide';

  return (
    <div className="win-app">
      {/* Barre de menu */}
      <div className="win-menubar">
        <span className="win-menu-item" style={{ position: "relative", cursor: 'pointer' }}
              data-testid="menu-demande"
              onClick={() => setShowDemandeMenu(v => !v)}
              onBlur={() => setTimeout(() => setShowDemandeMenu(false), 200)}
              tabIndex={0}>
          Demande
          {showDemandeMenu && (
            <div data-testid="menu-demande-dropdown"
                 style={{ position: "absolute", top: "100%", left: 0,
                          background: "#fff", border: "1px solid #808080",
                          boxShadow: "2px 2px 4px rgba(0,0,0,0.2)",
                          minWidth: 180, zIndex: 100, color: "#000",
                          fontWeight: 400 }}>
              <div data-testid="menu-demande-enregistrer-sous"
                   onClick={e => { e.stopPropagation();
                                    setShowDemandeMenu(false);
                                    handleEnregistrerSous(); }}
                   style={{ padding: "6px 20px", cursor: "pointer" }}
                   title="Crée une nouvelle demande à partir de la demande ouverte (même requérant)"
                   onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                   onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                Enregistrer sous…
              </div>
              <div data-testid="menu-demande-comparution"
                   onClick={e => { e.stopPropagation();
                                    setShowDemandeMenu(false);
                                    setShowComparution(true); }}
                   style={{ padding: "6px 20px", cursor: "pointer" }}
                   onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                   onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                Appel de comparution
              </div>
              <div data-testid="menu-demande-supprimer"
                   onClick={e => { e.stopPropagation();
                                    setShowDemandeMenu(false);
                                    handleSupprimerDemande(); }}
                   style={{ padding: "6px 20px", cursor: "pointer",
                            borderTop: "1px solid #d0d0d0", color: "#c00" }}
                   title="Supprime la demande ouverte (uniquement si aucun numéro de référence n'y est inscrit)"
                   onMouseEnter={e => e.currentTarget.style.background = "#fde8e8"}
                   onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                Supprimer
              </div>
            </div>
          )}
        </span>
        <span className="win-menu-item" style={{ position: "relative", cursor: 'pointer' }}
              data-testid="menu-statut"
              onClick={() => setShowStatutMenu(v => !v)}
              onBlur={() => setTimeout(() => setShowStatutMenu(false), 200)}
              tabIndex={0}>
          Statut
          {showStatutMenu && (
            <div data-testid="menu-statut-dropdown"
                 style={{ position: "absolute", top: "100%", left: 0,
                          background: "#fff", border: "1px solid #808080",
                          boxShadow: "2px 2px 4px rgba(0,0,0,0.2)",
                          minWidth: 240, zIndex: 100, color: "#000",
                          fontWeight: 400 }}>
              <div data-testid="menu-statut-fermeture-lot"
                   onClick={e => { e.stopPropagation();
                                    setShowStatutMenu(false);
                                    setShowFermetureLot(true); }}
                   style={{ padding: "6px 20px", cursor: "pointer" }}
                   onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                   onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                Fermeture des demandes en lot
              </div>
              <div data-testid="menu-statut-reactivation"
                   onClick={e => { e.stopPropagation();
                                    setShowStatutMenu(false);
                                    setShowReactivation(true); }}
                   style={{ padding: "6px 20px", cursor: "pointer",
                            borderTop: "1px solid #d0d0d0" }}
                   onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                   onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                Réactivation des demandes fermées
              </div>
              <div data-testid="menu-statut-questionnaire-lecture"
                   onClick={e => { e.stopPropagation();
                                    setShowStatutMenu(false);
                                    if (!formData?.no_sequence) {
                                      showPopup?.("Aucun dossier ouvert.", "warning", "Questionnaire de vérification");
                                      return;
                                    }
                                    setShowQuestionnaireLecture(true); }}
                   style={{ padding: "6px 20px", cursor: "pointer",
                            borderTop: "1px solid #d0d0d0" }}
                   onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                   onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                Questionnaire de vérification (lecture)
              </div>
            </div>
          )}
        </span>
        <span className="win-menu-item" style={{ position: "relative", cursor: 'pointer' }}
              data-testid="menu-outils"
              onClick={() => setShowOutilsMenu(v => !v)}
              onBlur={() => setTimeout(() => setShowOutilsMenu(false), 200)}
              tabIndex={0}>
          Outils
          {showOutilsMenu && (
            <div data-testid="menu-outils-dropdown"
                 style={{ position: "absolute", top: "100%", left: 0,
                          background: "#fff", border: "1px solid #808080",
                          boxShadow: "2px 2px 4px rgba(0,0,0,0.2)",
                          minWidth: 200, zIndex: 100, color: "#000",
                          fontWeight: 400 }}>
              <div data-testid="menu-outils-changer-bureau"
                   onClick={e => { e.stopPropagation();
                                    setShowOutilsMenu(false);
                                    handleChangerBureau(); }}
                   style={{ padding: "6px 20px", cursor: "pointer" }}
                   onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                   onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                Changer de bureau
              </div>
              <div data-testid="menu-outils-calculatrice"
                   onClick={e => { e.stopPropagation();
                                    setShowOutilsMenu(false);
                                    setShowCalculatrice(true); }}
                   style={{ padding: "6px 20px", cursor: "pointer",
                            borderTop: "1px solid #d0d0d0" }}
                   onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                   onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                Calculatrice
              </div>
              {canJumelage && (
                <div data-testid="menu-outils-jumelage"
                     onClick={e => { e.stopPropagation();
                                      setShowOutilsMenu(false);
                                      setShowJumelage(true); }}
                     style={{ padding: "6px 20px", cursor: "pointer",
                              borderTop: "1px solid #d0d0d0" }}
                     onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                     onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  Jumelage
                </div>
              )}
            </div>
          )}
        </span>
        <span className="win-menu-item" style={{ position: "relative", cursor: 'pointer' }}
              data-testid="menu-impression"
              onClick={() => {
                const opening = !showImpressionSub;
                setShowImpressionSub(opening);
                if (opening) {
                  const noseq = formData.no_sequence || currentDossier?.no_seq_daj || '';
                  setImpressionCtx(null);
                  if (noseq) {
                    fetch(`${API}/daj/${encodeURIComponent(noseq)}/impression-context`)
                      .then(r => r.ok ? r.json() : null)
                      .then(d => setImpressionCtx(d))
                      .catch(() => setImpressionCtx(null));
                  }
                }
              }}
              onBlur={() => setTimeout(() => setShowImpressionSub(false), 200)}
              tabIndex={0}>
          Impression
          {showImpressionSub && (
            <div data-testid="menu-impression-dropdown"
                 style={{ position: "absolute", top: "100%", left: 0,
                          background: "#fff", border: "1px solid #808080",
                          boxShadow: "2px 2px 4px rgba(0,0,0,0.2)",
                          minWidth: 260, zIndex: 100, color: "#000",
                          fontWeight: 400 }}>
              {(() => {
                const noseq = formData.no_sequence || currentDossier?.no_seq_daj || '';
                const reg = authUser?.region ? `&region=${encodeURIComponent(authUser.region)}` : '';
                const hRegion = formData.region || authUser?.region || '';
                const hBureau = formData.bureau || '';
                const ctx = impressionCtx;
                const ini = ctx?.initiales || '';
                const mkName = (base, id) => {
                  const parts = [base];
                  const idv = (id || '').toString().trim();
                  if (idv) parts.push(idv);
                  if (ini) parts.push(ini);
                  return parts.join('_') + '.pdf';
                };
                const openUrl = (url) => { setShowImpressionSub(false); window.open(url, '_blank'); };
                const openReport = (url, title, fileName) => {
                  setShowImpressionSub(false);
                  openReportTab({ url, title, fileName });
                };
                // Liste fixe câblée aux générateurs existants. Langue = adda209
                // (résolue côté serveur pour le Consentement).
                const items = [
                  { id: 'daj', label: 'DAJ', enabled: !!noseq, title: `DAJ — ${noseq}`,
                    url: `${API}/daj/${encodeURIComponent(noseq)}/print-view` },
                  { id: 'attestation', label: 'Attestation',
                    enabled: !!ctx?.attestation?.available,
                    title: 'Attestation',
                    fileName: mkName('ATTESTATION', ctx?.attestation?.noref),
                    url: ctx?.attestation?.available
                      ? `${API}/attestation/pdf?noref=${encodeURIComponent(ctx.attestation.noref)}&regbur=${encodeURIComponent(ctx.attestation.regbur)}`
                      : null },
                  { id: 'refus', label: 'Refus',
                    enabled: !!ctx?.refus?.available,
                    title: 'Refus',
                    fileName: mkName('REFUS', ctx?.refus?.noref),
                    url: ctx?.refus?.available
                      ? `${API}/refus-pdf?noref=${encodeURIComponent(ctx.refus.noref)}&regbur=${encodeURIComponent(ctx.refus.regbur)}`
                      : null },
                  { id: 'revision', label: 'Demande de révision',
                    enabled: !!ctx?.revision?.available,
                    title: 'Demande de révision',
                    fileName: mkName('REVISION', ctx?.revision?.noref),
                    url: ctx?.revision?.available
                      ? `${API}/revision-pdf?noref=${encodeURIComponent(ctx.revision.noref)}&regbur=${encodeURIComponent(ctx.revision.regbur)}`
                      : null },
                  { id: 'consentement', label: 'Consentement', enabled: !!noseq,
                    title: 'Consentement',
                    fileName: mkName('CONSENTEMENT', noseq),
                    url: `${API}/impression/${encodeURIComponent(noseq)}/consentement?conjoint=0${reg}` },
                  { id: 'consentement-conjoint', label: 'Consentement conjoint(e)',
                    enabled: !!ctx?.has_conjoint,
                    title: 'Consentement conjoint(e)',
                    fileName: mkName('CONSENTEMENTCONJOINT', noseq),
                    url: ctx?.has_conjoint
                      ? `${API}/impression/${encodeURIComponent(noseq)}/consentement?conjoint=1${reg}`
                      : null },
                  { id: 'substitution', label: 'Substitution procureur',
                    enabled: !!ctx?.substitution?.available,
                    title: 'Substitution procureur',
                    fileName: mkName('SUBSTITUTION', ctx?.substitution?.noref),
                    url: ctx?.substitution?.available
                      ? `${API}/attestation/${encodeURIComponent(ctx.substitution.noref)}/substitution-pdf?regbur=${encodeURIComponent(ctx.substitution.regbur)}&dest=bureau`
                      : null },
                  { id: 'partie-adverse', label: 'Partie adverse',
                    enabled: !!ctx?.partie_adverse?.available,
                    title: 'Partie adverse',
                    fileName: mkName('PARTIEADVERSE', noseq),
                    url: ctx?.partie_adverse?.available
                      ? `${API}/daj/${encodeURIComponent(noseq)}/parties-adverses-pdf`
                      : null },
                  { id: 'liste-demandes', label: 'Liste des demandes pour le requérant',
                    enabled: !!noseq,
                    title: 'Liste des demandes pour le requérant',
                    fileName: mkName('LISTEDEMANDES', noseq),
                    url: `${API}/daj/${encodeURIComponent(noseq)}/liste-demandes-pdf` },
                  { id: 'liens', label: 'Liens',
                    enabled: !!ctx?.liens?.available,
                    title: 'Liens',
                    fileName: mkName('LIENS', noseq),
                    url: ctx?.liens?.available
                      ? `${API}/daj/${encodeURIComponent(noseq)}/liens-pdf`
                      : null },
                  // Rapport autonome (ne dépend d'aucune demande ouverte)
                  { id: 'homolog-60j', label: 'Liste des homologations de plus de 60 jours',
                    standalone: true, enabled: !!(hRegion && hBureau),
                    title: 'Liste des homologations de plus de 60 jours',
                    fileName: 'HOMOLOGATIONS60J.pdf',
                    url: `${API}/homologations-60j-pdf?region=${encodeURIComponent(hRegion)}&bureau=${encodeURIComponent(hBureau)}` },
                ];
                return items.map(it => (
                  <div key={it.id} data-testid={`menu-impression-${it.id}`}
                       onClick={e => { e.stopPropagation(); if (it.enabled) {
                         if (it.id === 'daj') openUrl(it.url);
                         else openReport(it.url, it.title, it.fileName);
                       } }}
                       title={it.enabled ? '' : (it.standalone ? '' : 'Non disponible pour ce dossier')}
                       style={{ padding: "6px 20px",
                                borderTop: it.standalone ? "1px solid #d0d0d0" : "none",
                                marginTop: it.standalone ? 4 : 0,
                                cursor: it.enabled ? "pointer" : "not-allowed",
                                color: it.enabled ? "#000" : "#aaa" }}
                       onMouseEnter={e => { if (it.enabled) e.currentTarget.style.background = "#e0e7ff"; }}
                       onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    {it.label}
                  </div>
                ));
              })()}
            </div>
          )}
        </span>
        <span className="win-menu-item" style={{ position: "relative", cursor: 'pointer' }}
              data-testid="menu-formulaires"
              onClick={() => {
                const opening = !showFormulairesMenu;
                setShowFormulairesMenu(opening);
                if (opening) {
                  fetch(`${API}/rapports/menu`)
                    .then(r => r.json())
                    .then(d => setFormulairesRapports(Array.isArray(d) ? d : []))
                    .catch(() => setFormulairesRapports([]));
                }
              }}
              onBlur={() => setTimeout(() => setShowFormulairesMenu(false), 200)}
              tabIndex={0}>
          Formulaires
          {showFormulairesMenu && (
            <div data-testid="menu-formulaires-dropdown"
                 style={{ position: "absolute", top: "100%", left: 0,
                          background: "#fff", border: "1px solid #808080",
                          boxShadow: "2px 2px 4px rgba(0,0,0,0.2)",
                          minWidth: 220, zIndex: 100, color: "#000",
                          fontWeight: 400 }}>
              {formulairesRapports.length === 0 && (
                <div style={{ padding: "6px 20px", color: "#888", fontSize: 13 }}>
                  Aucun rapport disponible
                </div>
              )}
              {formulairesRapports.map(r => (
                <div key={r.id} data-testid={`menu-formulaire-rapport-${r.id}`}
                     onClick={e => { e.stopPropagation();
                                      setShowFormulairesMenu(false);
                                      const nom = r.nom || '';
                                      // Formulaires vierges multilingues → choix de langue.
                                      if (nom.startsWith('Réciprocité')) {
                                        setReciprociteChoice({ id: r.id, title: 'Réciprocité', langs: ['fr', 'en'] });
                                        return;
                                      }
                                      if (nom.startsWith("Demande d'aide")) {
                                        setReciprociteChoice({ id: r.id, title: 'Demande', langs: ['fr', 'en', 'es'] });
                                        return;
                                      }
                                      if (nom.startsWith('Autorisation et consentement')) {
                                        setReciprociteChoice({ id: r.id, title: 'Autorisation et consentement', langs: ['fr', 'en', 'es'] });
                                        return;
                                      }
                                      if (nom.startsWith('Révision')) {
                                        setReciprociteChoice({ id: r.id, title: 'Révision', langs: ['fr', 'en'] });
                                        return;
                                      }
                                      if (nom.startsWith('Entente de paiement')) {
                                        setReciprociteChoice({ id: r.id, title: 'Entente de paiement', langs: ['fr', 'en'] });
                                        return;
                                      }
                                      // Formulaire vierge : aucune donnée client (pas de noseqdaj).
                                      const params = [];
                                      if (authUser?.region) params.push(`region=${encodeURIComponent(authUser.region)}`);
                                      const q = params.length ? `?${params.join('&')}` : '';
                                      window.open(`${API}/rapports/${r.id}/pdf${q}`, '_blank'); }}
                     style={{ padding: "6px 20px", cursor: "pointer" }}
                     onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                     onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  {(r.nom || '').startsWith("Demande d'aide") ? 'Demande' : r.nom}
                </div>
              ))}
            </div>
          )}
        </span>
        <span className="win-menu-item">Aide</span>
        {isAdmin && (
        <span className="win-menu-item" style={{ position: "relative", cursor: 'pointer' }}
              data-testid="menu-admin"
              onClick={() => { setShowAdminMenu(v => !v); setShowImpressionSub(false); }}
              onBlur={() => setTimeout(() => { setShowAdminMenu(false); setShowImpressionSub(false); }, 200)}
              tabIndex={0}>
          Admin
          {showAdminMenu && (
            <div data-testid="menu-admin-dropdown"
                 style={{ position: "absolute", top: "100%", left: 0,
                          background: "#fff", border: "1px solid #808080",
                          boxShadow: "2px 2px 4px rgba(0,0,0,0.2)",
                          minWidth: 220, zIndex: 100, color: "#000",
                          fontWeight: 400 }}>
              {isAdmin && (
                <div data-testid="menu-admin-utilisateurs"
                     onClick={e => { e.stopPropagation();
                                      setShowAdminMenu(false);
                                      setShowGestionUtilisateurs(true); }}
                     style={{ padding: "6px 20px", cursor: "pointer" }}
                     onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                     onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  Gestion des utilisateurs
                </div>
              )}
              {isAdmin && (
                <div data-testid="menu-admin-catqverif"
                     onClick={e => { e.stopPropagation();
                                      setShowAdminMenu(false);
                                      setShowGestionCatQVerif(true); }}
                     style={{ padding: "6px 20px", cursor: "pointer" }}
                     onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                     onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  Questionnaire de vérification
                </div>
              )}
              {isAdmin && (
                <div data-testid="menu-admin-qentrevue"
                     onClick={e => { e.stopPropagation();
                                      setShowAdminMenu(false);
                                      setShowGestionQEntrevue(true); }}
                     style={{ padding: "6px 20px", cursor: "pointer" }}
                     onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                     onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  Questionnaire d'entrevue
                </div>
              )}
              {isAdmin && (
                <div data-testid="menu-admin-bureaux-eloignes"
                     onClick={e => { e.stopPropagation();
                                      setShowAdminMenu(false);
                                      setShowBureauxEloignes(true); }}
                     style={{ padding: "6px 20px", cursor: "pointer" }}
                     onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                     onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  Bureaux région éloignée
                </div>
              )}
              {isAdmin && (
                <div data-testid="menu-admin-gestion-rapports"
                     onClick={e => { e.stopPropagation();
                                      setShowAdminMenu(false);
                                      setShowGestionRapports(true); }}
                     style={{ padding: "6px 20px", cursor: "pointer" }}
                     onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                     onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  Gestion des rapports
                </div>
              )}
              {isAdmin && (
                <div data-testid="menu-admin-jurisevolution"
                     onClick={e => { e.stopPropagation();
                                      setShowAdminMenu(false);
                                      setShowJEConfig(true); }}
                     style={{ padding: "6px 20px", cursor: "pointer" }}
                     onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                     onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  Configuration JurisÉvolution
                </div>
              )}
              <div style={{ borderTop: "1px solid #d0d0d0" }}>
                <a data-testid="menu-schema-bd"
                   href="/db-diagram.html"
                   target="_blank"
                   rel="noopener noreferrer"
                   onClick={e => { e.stopPropagation(); setShowAdminMenu(false); }}
                   style={{ display: "block", padding: "6px 20px", cursor: "pointer",
                            textDecoration: "none", color: "inherit" }}
                   onMouseEnter={e => e.currentTarget.style.background = "#e0e7ff"}
                   onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  Schéma BD
                </a>
              </div>
            </div>
          )}
        </span>
        )}
        {process.env.REACT_APP_ENV_LABEL && (
          <span
            data-testid="env-badge-menubar"
            style={{
              alignSelf: 'center', marginLeft: '10px', padding: '1px 10px',
              fontSize: '11px', fontWeight: 700, letterSpacing: '2px',
              color: '#fff', background: '#d97706', borderRadius: '4px',
              whiteSpace: 'nowrap'
            }}
          >
            {process.env.REACT_APP_ENV_LABEL}
          </span>
        )}
        {authUser && (
          <span
            data-testid="menu-user-info"
            style={{
              marginLeft: 'auto', fontSize: '13px', display: 'flex',
              gap: '8px', alignItems: 'center', whiteSpace: 'nowrap',
              paddingRight: '10px', color: '#ffffff', fontWeight: 400
            }}
          >
            {authUser.nom_affichage || authUser.courriel} — Région {authUser.region}{currentBureau ? ` / Bureau ${currentBureau}` : ''}
            {onLogout && (
              <button
                data-testid="btn-logout"
                onClick={onLogout}
                style={{
                  fontSize: '13px', padding: '1px 8px', cursor: 'pointer',
                  background: '#f4c7c3', border: '1px solid #c0625c', color: '#7a1f1a'
                }}
              >Déconnexion</button>
            )}
          </span>
        )}
        {/* Indicateurs de date à droite : version app + dernière modif du dossier */}
        <span
          data-testid="app-version-info"
          style={{
            marginLeft: authUser ? '0' : 'auto',
            fontSize: '13px',
            opacity: 0.85,
            display: 'flex',
            gap: '14px',
            alignItems: 'center',
            paddingRight: '8px',
            color: '#ffffff',
            whiteSpace: 'nowrap'
          }}
        >
          {dossierDateModif && (
            <span data-testid="dossier-date-modif" title="Dernière modification du dossier">
              📝 Dossier modifié : {String(dossierDateModif).substring(0, 16).replace('T', ' ')}
            </span>
          )}
          <span data-testid="app-version" title="Version de l'application">
            ⚙ Version : {appVersionDt}
          </span>
        </span>
      </div>

      {/* Barre d'outils */}
      <div className="win-toolbar">
        <WinButton IconComponent={FilePlus} onClick={handleNouveau} title="Nouveau dossier" data-testid="btn-nouveau">Nouveau</WinButton>
        <WinButton IconComponent={ClipboardList} onClick={() => setShowFir(true)} title="Fiche Initiale de Renseignements (entrevue)" data-testid="btn-fir">Entrevue</WinButton>
        <WinButton IconComponent={Search} onClick={handleRechercher} title="Rechercher" data-testid="btn-rechercher">Rechercher</WinButton>

        <WinButton IconComponent={Save} onClick={handleEnregistrer} disabled={isDisabled} title="Enregistrer" data-testid="btn-enregistrer">Enregistrer</WinButton>
        <WinButton IconComponent={SaveAll} onClick={handleEnregistrerSous} disabled={isDisabled} title="Enregistrer sous (nouvelle demande pour le même requérant)" data-testid="btn-enregistrer-sous">Enregistrer sous</WinButton>
        <div className="win-toolbar-separator" />
        <WinButton IconComponent={Printer} disabled={isDisabled} title="Imprimer le formulaire DAJ" data-testid="btn-imprimer" onClick={async () => {
          const seq = formData.no_sequence || currentDossier?.no_seq_daj;
          if (!seq) { alert("Veuillez d'abord ouvrir un dossier."); return; }
          // Dialogue de sélection des rapports (parité legacy frmDAJPrint).
          const c = formData.conjoint || {};
          const art = (c.art_6_ou_7 || "").trim();
          const conjointAvailable = !!(String(c.nom || "").trim() && String(c.prenom || "").trim()
            && String(c.date_naissance || "").trim() && art !== "7(1)" && art !== "7(2)");
          setDajPrint({ open: true, seq, conjointAvailable });
        }}>Imprimer</WinButton>
        <WinButton IconComponent={Calculator} onClick={() => setShowCalculatrice(true)} title="Calculer l'admissibilité" data-testid="btn-calculatrice">Calculatrice</WinButton>
        <div className="win-toolbar-separator" />
        <WinButton IconComponent={Scale} onClick={handleOuvrirHomolog} disabled={isDisabled} title="Homologation" data-testid="btn-homologation">Homologation</WinButton>
        <WinButton IconComponent={UserCircle2} onClick={() => setShowAvocats(true)} title="Rechercher un avocat et voir ses mandats" data-testid="btn-avocats">Avocats</WinButton>
        <WinButton IconComponent={Stethoscope} title="Expertise du dossier" data-testid="btn-expertise" disabled={isDisabled}
                   onClick={() => {
                     if (!formData?.no_sequence) {
                       showPopup?.("Aucun dossier ouvert.", "warning", "Expertise");
                       return;
                     }
                     setShowExpertise(true);
                   }}>Expertise</WinButton>
        <WinButton IconComponent={Users} onClick={() => setShowAdverse(true)} disabled={isDisabled} title="Parties adverses du dossier" data-testid="btn-adverse">Adverse</WinButton>
        <WinButton IconComponent={jeSyncing ? Loader2 : UploadCloud} onClick={handleSyncJurisEvolution} disabled={jeSyncing || isDisabled || !formData.no_sequence} title="Créer ou mettre à jour le dossier dans JurisÉvolution" data-testid="btn-jurisevolution">{jeSyncing ? 'En cours…' : 'Juris'}</WinButton>
        <div className="win-toolbar-separator" />
        <WinButton IconComponent={FileCheck} onClick={handleOuvrirAttestation} disabled={isDisabled} title="Attestation d'aide juridique" data-testid="btn-attestation">Attestation</WinButton>
        <WinButton IconComponent={XCircle} onClick={handleOuvrirRefus} disabled={isDisabled} title="Refus d'aide juridique" data-testid="btn-refus">Refus</WinButton>
        <div className="win-toolbar-separator" />
        <WinButton IconComponent={ChevronsLeft} title="Début — demande la plus ancienne" onClick={() => navigateImage('debut')} disabled={!formData.no_sequence} data-testid="btn-debut">Début</WinButton>
        <WinButton IconComponent={ChevronLeft} title="Précédent — demande plus ancienne" onClick={() => navigateImage('precedent')} disabled={!formData.no_sequence} data-testid="btn-precedent">Précédent</WinButton>
        <WinButton IconComponent={ChevronRight} title="Suivant — demande plus récente" onClick={() => navigateImage('suivant')} disabled={!formData.no_sequence} data-testid="btn-suivant">Suivant</WinButton>
        <WinButton IconComponent={ChevronsRight} title="Fin — demande la plus récente" onClick={() => navigateImage('fin')} disabled={!formData.no_sequence} data-testid="btn-fin">Fin</WinButton>
      </div>

      {/* En-tete dossier */}
      <div className="win-header-bar">
        <div className="win-header-field">
          <label>No. Dossier {formData.region === '18' && <span className="required-indicator">*</span>}</label>
          <WinInput value={formData.no_dossier} onChange={v => updateFormData('no_dossier', v)} disabled={isDisabled} width="100px" placeholder={formData.region === '18' ? 'Obligatoire' : ''} />
        </div>
        <div className="win-header-field">
          <label>No. Référence</label>
          {/* Toujours en lecture seule — généré côté serveur, jamais saisi par l'utilisateur */}
          <WinInput value={formData.no_reference} readOnly={true} width="150px" />
        </div>
        <div className="win-header-field">
          <label>Région</label>
          {/* Toujours en lecture seule — fixée à la création du dossier */}
          <WinInput value={formData.region} readOnly={true} width="40px" />
        </div>
        <div className="win-header-field">
          <label>Bureau</label>
          {/* Lecture seule une fois le dossier créé (no_sequence présent) */}
          <WinInput
            value={formData.bureau}
            onChange={v => updateFormData('bureau', v)}
            readOnly={!!formData.no_sequence}
            disabled={isDisabled}
            width="40px"
          />
        </div>
        <div className="win-header-field">
          <label>No. Séquence</label>
          <WinInput value={formData.no_sequence} readOnly={true} width="150px" />
        </div>
        <div className="win-header-field">
          <label>Date Demande</label>
          {/* Règles de modification :
              1. numéro de référence présent → date NON modifiable
              2. adda235=1 (Enregistrer sous) → date JAMAIS modifiable
              3. adda235=0 ET sans référence → date modifiable */}
          {(() => {
            const hasRef = !!(formData.no_reference && String(formData.no_reference).trim());
            const locked = hasRef || Number(formData.adda235) === 1;
            return (locked || isDisabled) ? (
              <WinInput
                value={formData.date_demande || ''}
                readOnly={true}
                disabled={isDisabled}
                width="130px"
                data-testid="header-date-demande-locked"
              />
            ) : (
              <DateInput
                value={formData.date_demande}
                onChange={v => updateFormData('date_demande', v || todayLocal())}
                max={todayLocal()}
                style={{ width: 130 }}
                testId="header-date-demande"
              />
            );
          })()}
        </div>
        <div className="win-header-field">
          <label>Statut</label>
          {/* Statut affiché — même format que StatutTab.jsx `displayStatut` :
              `addastatut / chemine` où chemine = infoManqStatut ||
              (dossier_ferme ? 'FERMÉ' : (formData.statut || 'EN COURS')).
              Couleur selon `statut_code_r` : 1=orange, 2=bleu, 3=vert, 4=violet. */}
          {(() => {
            const chemine = dossierStatut.label
              || (formData?.dossier_ferme ? 'FERMÉ' : (formData?.statut || (formData?.no_sequence ? 'EN COURS' : '')));
            const label = dossierStatut.addastatut
              ? `${dossierStatut.addastatut} / ${chemine}`
              : chemine;
            const code = dossierStatut.code;
            return (
              <div
                data-testid="header-dossier-statut"
                style={{
                  width: 260,
                  minHeight: 20,
                  padding: '2px 6px',
                  fontSize: 11,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRadius: 3,
                  border: '1px solid #666',
                  background:
                    code === 4 ? '#e1bee7' :
                    code === 3 ? '#c8e6c9' :
                    code === 2 ? '#bbdefb' :
                    code >= 1 ? '#ffe0b2' : '#eeeeee',
                  color:
                    code === 4 ? '#4527a0' :
                    code === 3 ? '#0a7d0a' :
                    code === 2 ? '#0a5a80' :
                    code >= 1 ? '#c07000' : '#546e7a',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={label || 'Aucun dossier ouvert'}
              >
                {label || '—'}
              </div>
            );
          })()}
        </div>
        {sansEntrevue && formData.no_sequence ? (
          <div data-testid="header-sans-entrevue"
               style={{ alignSelf: 'flex-end', marginLeft: 8, marginBottom: 1, padding: '3px 10px',
                        fontSize: 11, fontWeight: 700, color: '#8a4b00', background: '#fff3cd',
                        border: '1px solid #d9a12a', borderRadius: 3, whiteSpace: 'nowrap' }}>
            Demande créée sans le mode entrevue
          </div>
        ) : null}
        {/* icône globe retirée à la demande de l'utilisateur */}
      </div>

      {/* Onglets */}
      <div className="win-tabs">
        {tabs.map(tab => {
          const tabDisabled = isDisabled || (revenuActifDisabled && (tab.id === 'revenus' || tab.id === 'actifs'));
          return (
          <button key={tab.id} className={`win-tab ${currentTab === tab.id ? 'active' : ''} ${tabDisabled ? 'disabled' : ''}`} onClick={() => !tabDisabled && setCurrentTab(tab.id)} data-testid={`tab-${tab.id}`} disabled={tabDisabled}>
            {tab.label}
          </button>
          );
        })}
        {clientSelectionne && editMode !== 'vide' && (
          <span data-testid="banner-client-selectionne"
                style={{ marginLeft: "auto", alignSelf: "center", padding: "3px 14px",
                         marginBottom: 6, background: "#b00020", color: "#fff",
                         fontWeight: 700, fontSize: 13, letterSpacing: "0.5px",
                         borderRadius: 3, whiteSpace: "nowrap" }}>
            LE CLIENT A ÉTÉ SÉLECTIONNÉ
          </span>
        )}
      </div>

      {/* Contenu des onglets */}
      <div className="win-content">
        {editMode === 'vide' ? (
          <div className="win-empty-state">
            <div className="win-empty-icon">📁</div>
            <h2>Aucun dossier ouvert</h2>
            <p>Cliquez sur <button type="button" className="win-empty-link" onClick={handleNouveau} data-testid="empty-link-nouveau">Nouveau</button> pour créer un dossier ou <button type="button" className="win-empty-link" onClick={handleRechercher} data-testid="empty-link-rechercher">Rechercher</button> pour ouvrir un dossier existant.</p>
          </div>
        ) : (
          <form autoComplete="off" onSubmit={e => e.preventDefault()}>
            {currentTab === 'requerant' && <RequerantTab formData={formData} updateFormData={updateFormData} setFormData={setFormData} provinces={provinces} categories={categories} nasError={nasError} setNasError={setNasError} setMessage={setMessage} showPopup={showPopup} handleVerifierRefusPrecedent={handleVerifierRefusPrecedent} onTransfertToggle={handleTransfertToggle} onReciproToggle={handleReciproToggle} onRevisionToggle={handleRevisionToggle} onEditRevisionNote={() => setShowNoteRevision(true)} setShowEtablissement={setShowEtablissement} etablissementSelectionne={etablissementSelectionne} clearEtablissement={() => { setEtablissementSelectionne(null); setFormData(prev => ({ ...prev, adresse: '', ville: '', province: 'QC', code_postal: '' })); setIsDirty(true); setMessage('Établissement retiré du dossier (adresse effacée) — n\'oubliez pas d\'enregistrer'); }} setCurrentTab={setCurrentTab} currentDossier={currentDossier} onDateNaissanceLeave={handleDateNaissanceLeave} />}
            {currentTab === 'conjoint' && <ConjointTab formData={formData} updateConjoint={updateConjoint} provinces={provinces} handleMemeAdresseConjoint={handleMemeAdresseConjoint} handleVerifInteretsOpposes={handleVerifInteretsOpposes} handleSupprimerConjoint={handleSupprimerConjoint} noseqdaj={formData.no_sequence || currentDossier?.no_seq_daj || ''} showPopup={showPopup} clearConjointRevenus={clearConjointRevenus} />}
            {currentTab === 'liens' && <LiensTab formData={formData} provinces={provinces} showGestionLiens={showGestionLiens} setShowGestionLiens={setShowGestionLiens} loadLiensFromServer={loadLiensFromServer} liensListe={liensListe} isLoadingLiens={isLoadingLiens} lienEnEdition={lienEnEdition} setLienEnEdition={setLienEnEdition} handleNouveauLien={handleNouveauLien} handleEditerLien={handleEditerLien} handleSauvegarderLien={handleSauvegarderLien} handleSupprimerLienServeur={handleSupprimerLienServeur} handleDefinirCommeLienExclusif={handleDefinirCommeLienExclusif} showPopup={showPopup} />}
            {currentTab === 'revenus' && <RevenusTab formData={formData} updateRevenus={updateRevenus} setFormData={setFormData} totalRevenusReq={totalRevenusReq} totalRevenusFam={totalRevenusFam} totalDeductions={totalDeductions} noSeqDaj={formData.no_sequence || currentDossier?.no_seq_daj || ''} />}
            {currentTab === 'actifs' && <ActifsTab formData={formData} updateActifs={updateActifs} />}
            {currentTab === 'service' && <ServiceTab formData={formData} updateService={updateService} setFormData={setFormData} handleUncheckMemeAffaire={handleUncheckMemeAffaire} />}
            {currentTab === 'decision' && <DecisionTab formData={formData} updateDecision={updateDecision} />}
            {currentTab === 'statut' && <StatutTab formData={formData} setFormData={setFormData} setCurrentTab={setCurrentTab} onMissingItemsChange={setMissingItems} onStatutChange={setDossierStatut} />}
          </form>
        )}
      </div>

      {/* Barre de statut */}
      <div className="win-statusbar">
        <span className="win-status-message">
          {editMode === 'vide' ? 'En attente...' : editMode === 'nouveau' ? 'Nouveau dossier' : `Edition: ${formData.prenom} ${formData.nom}`}
        </span>
        <span className="win-status-stats">Clients: {stats.total_clients} | Dossiers: {stats.total_daj}</span>
      </div>

      {/* Modals */}
      {isSearching && <SearchModal searchResults={searchResults} handleSelectClient={handleSelectClient} onClose={() => setIsSearching(false)} />}
      {showRecherche && (
        <RechercheModal
          onClose={() => setShowRecherche(false)}
          onSelect={handleSelectRechercheRow}
          defaultNoBur={formData.bureau || defaultBureau}
          defaultNoReg={formData.region || defaultRegion}
          bureauxAutorises={[...new Set([authUser?.bureau, ...(authUser?.bureaux || [])].map(b => (b || '').trim()).filter(Boolean))]}
          isAdmin={isAdmin}
        />
      )}
      {showAvocats && (
        <AvocatsModal
          onClose={() => setShowAvocats(false)}
          onSelectDossier={(row) => { setShowAvocats(false); handleSelectRechercheRow(row); }}
        />
      )}
      {showCalculatrice && <CalculatriceModal onClose={() => setShowCalculatrice(false)} />}
      {showGestionQEntrevue && (
        <GestionQuestionnaireEntrevueModal
          onClose={() => setShowGestionQEntrevue(false)}
          showPopup={showPopup} />
      )}
      {showFir && <FIRModal open={showFir} initialFirId={firInitialId}
                            onClose={() => { setShowFir(false); setFirInitialId(null); }} />}
      {showJumelage && <JumelageModal onClose={() => setShowJumelage(false)} />}
      {showFermetureLot && (
        <FermetureLotModal onClose={() => setShowFermetureLot(false)}
                           noReg={defaultRegion} noBur={currentBureau} />
      )}
      {showNoteRevision && (
        <NoteRevisionModal
          noteInitiale={formData.revision_note || ''}
          onRetour={handleNoteRevisionRetour} />
      )}
      {showProvRecipro && (
        <ProvReciproModal
          provinces={provinces}
          onOk={(prov) => appliquerReciprocite(prov)}
          onAnnuler={() => setShowProvRecipro(false)}
          showPopup={showPopup} />
      )}
      {showTransfertModal && (
        <TransfertModal
          onClose={() => setShowTransfertModal(false)}
          onValider={handleTransfertValide}
          codeNature={formData.service?.code_nature || ''}
          showPopup={showPopup} />
      )}
      {showQuestionnaireLecture && (
        <QuestionnaireLectureModal
          noSequence={formData.no_sequence}
          noReg={formData.region}
          noBur={formData.bureau}
          onClose={() => setShowQuestionnaireLecture(false)} />
      )}
      {showReactivation && (
        <ReactivationModal onClose={() => setShowReactivation(false)}
                           noBur={currentBureau}
                           defaultNoSeq={formData?.no_sequence || ""} />
      )}
      {showModeDemande && (
        <ModeDemandeModal onChoisir={handleModeDemandeChoisi}
                          onCancel={() => setShowModeDemande(false)} />
      )}
      {pendingNouveau && (
        <QuestionnaireModal client={pendingNouveau.client}
                            requerant={{ nom: nouveauForm.nom, prenom: nouveauForm.prenom, date_naissance: nouveauForm.date_naissance }}
                            ctaLabel={modeDemande === 'reguliere' ? 'Créer la demande' : "Créer l'entrevue"}
                            onNonValide={handleQuestionnaireNon}
                            onCancel={() => setPendingNouveau(null)} />
      )}
      {showGestionUtilisateurs && (
        <GestionUtilisateursModal onClose={() => setShowGestionUtilisateurs(false)} />
      )}
      {showGestionCatQVerif && (
        <GestionCategoriesQVerifModal onClose={() => setShowGestionCatQVerif(false)} showPopup={showPopup} />
      )}
      {showBureauxEloignes && (
        <GestionBureauxEloignesModal onClose={() => setShowBureauxEloignes(false)}
                                     showPopup={showPopup} onChanged={loadBureauxEloignes} />
      )}
      {showChangerBureau && (
        <ChangerBureauModal
          authUser={authUser}
          currentBureau={currentBureau}
          onSelect={(b) => {
            setCurrentBureau(b);
            if (editMode === 'vide') setFormData(prev => ({ ...prev, bureau: b }));
          }}
          onClose={() => setShowChangerBureau(false)}
        />
      )}
      <PartieAdverseModal
        isOpen={showAdverse}
        onClose={() => setShowAdverse(false)}
        pkDaj={currentDossier?.pk_daj || currentDossier?.no_seq_daj || formData.no_sequence || ""}
        showPopup={showPopup}
        formData={formData}
        requerantNom={formData.nom}
        requerantPrenom={formData.prenom}
      />
      <AttestationModal
        isOpen={showAttestation}
        onClose={() => setShowAttestation(false)}
        isAdmin={isAdmin}
        canSupprimer={canSupprimer}
        pkDaj={currentDossier?.pk_daj || currentDossier?.no_seq_daj || formData.no_sequence || ""}
        noclient={formData.noclient || currentDossier?.noclient || ""}
        showPopup={showPopup}
        formData={formData}
        requerantNom={formData.nom}
        requerantPrenom={formData.prenom}
        onAttestationCreated={(noref, linkedSeqList) => {
          // Cas SUPPRESSION : le modal appelle avec noref="" et []. On nettoie
          // le champ NO. RÉFÉRENCE du formulaire principal si le dossier courant
          // correspondait à l'attestation supprimée (backend a cascadé
          // daj.noref="" en DB — parité `frmPwdDelAttest.vb:1698`).
          if (!noref) {
            setFormData(prev => ({ ...prev, no_reference: "" }));
            if (currentDossier) setCurrentDossier(prev => prev ? { ...prev, no_ref: "", noref: "" } : prev);
            return;
          }
          // Le backend a cascadé daj.noref pour chaque DAJ lié. Si le dossier
          // courant est dans la liste, on met à jour le champ NO. RÉFÉRENCE
          // affiché en haut du formulaire immédiatement (sans rechargement).
          const currentSeq = currentDossier?.no_seq_daj || formData.no_sequence;
          if (currentSeq && (linkedSeqList || []).includes(currentSeq)) {
            setFormData(prev => ({ ...prev, no_reference: noref }));
            if (currentDossier) setCurrentDossier(prev => prev ? { ...prev, no_ref: noref, noref: noref } : prev);
          }
        }}
      />      <RefusModal
        isOpen={showRefus}
        onClose={() => setShowRefus(false)}
        canSupprimer={canSupprimer}
        noclient={formData.noclient || currentDossier?.noclient || ""}
        showPopup={showPopup}
        formData={formData}
        requerantNom={formData.nom}
        requerantPrenom={formData.prenom}
        onRefusChanged={(noref, linkedSeqList) => {
          // Cas SUPPRESSION (noref vide) : nettoie le champ NO. RÉFÉRENCE si
          // le dossier courant correspondait au refus supprimé.
          if (!noref) {
            setFormData(prev => ({ ...prev, no_reference: "" }));
            if (currentDossier) setCurrentDossier(prev => prev ? { ...prev, no_ref: "", noref: "" } : prev);
            return;
          }
          // Cas CRÉATION : propage le noref au formulaire principal si le
          // dossier courant est dans les DAJ liés (parité `MDIParent2.txtNoRef`).
          const currentSeq = currentDossier?.no_seq_daj || formData.no_sequence;
          if (currentSeq && (linkedSeqList || []).includes(currentSeq)) {
            setFormData(prev => ({ ...prev, no_reference: noref }));
            if (currentDossier) setCurrentDossier(prev => prev ? { ...prev, no_ref: noref, noref: noref } : prev);
          }
        }}
      />
      <HomologModal
        isOpen={showHomolog}
        onClose={() => setShowHomolog(false)}
        formData={formData}
        currentDossier={currentDossier}
        showPopup={showPopup}
      />
      <ComparutionModal
        isOpen={showComparution}
        onClose={() => setShowComparution(false)}
        showPopup={showPopup}
        onOpenDemande={handleSelectRechercheRow}
      />
      <ExpertiseModal
        open={showExpertise}
        onClose={() => setShowExpertise(false)}
        dossier={{
          no_seq_daj: formData?.no_sequence,
          no_region: formData?.region,
          no_bureau: formData?.bureau,
          no_reference: formData?.no_reference,
          no_dossier: formData?.no_dossier_cour,
          no_client: formData?.noclient,
          // Avocat SIGNATAIRE de la demande (adda704 — cboAvoSign legacy)
          // → défaut pour l'avocat signataire de l'expertise. Requête
          // utilisateur explicite : "L'avocat signataire de la demande doit
          // être par défaut l'avocat signataire de l'expertise".
          dossier_confie_a: formData?.decision?.signature_avocat_code
            || formData?.decision?.dossier_confie_code
            || formData?.dossier_confie_a || '',
          avec_contribution: formData?.decision?.avec_contribution,
          gratuit: formData?.decision?.gratuit,
        }}
      />
      <EtablissementModal isOpen={showEtablissement} onClose={() => setShowEtablissement(false)} onSelect={(etab) => { setEtablissementSelectionne(etab); if (etab) { setFormData(prev => ({ ...prev, adresse: etab.adresse || prev.adresse, ville: etab.ville || prev.ville, province: etab.province || prev.province, code_postal: etab.code_postal || prev.code_postal })); } }} currentEtablis={etablissementSelectionne} />
      <RefusPrecedentModal isOpen={showRefusPrecedent} onClose={() => setShowRefusPrecedent(false)} data={refusPrecedentData} />

      <NouveauModal showNouveauModal={showNouveauModal} setShowNouveauModal={setShowNouveauModal} nouveauForm={nouveauForm} setNouveauForm={setNouveauForm} handleNouveauSearch={handleNouveauSearch} isSearchingDuplicates={isSearchingDuplicates} showDuplicateList={showDuplicateList} setShowDuplicateList={setShowDuplicateList} duplicateClients={duplicateClients} handleSelectExistingClient={handleSelectExistingClient} handleCreateNewAnyway={handleCreateNewAnyway} />
      <MessagePopup isOpen={popup.isOpen} onClose={closePopup} title={popup.title} message={popup.message} type={popup.type} details={popup.details} />
      <JurisConfirmModal
        open={jurisConfirm.open}
        matterId={jurisConfirm.matterId}
        fields={jurisConfirm.fields}
        onCancel={() => setJurisConfirm({ open: false, noSeq: '', matterId: null, fields: [] })}
        onConfirm={() => { const ns = jurisConfirm.noSeq; setJurisConfirm({ open: false, noSeq: '', matterId: null, fields: [] }); doSyncJuris(ns); }}
        currentUnavailable={jurisConfirm.currentUnavailable}
      />

      {/* Modal Nouvelle Image — parité legacy ValidParamImage (CodeDajNew.vb:295-356) */}
      {revisionModal.open && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2500}} data-testid="revision-modal-overlay">
          <div style={{backgroundColor: '#f0f0f0', border: '2px solid #1b6e7d', width: 520, fontSize: 12}} data-testid="revision-modal">
            <div style={{background: '#1b6e7d', color: '#fff', padding: '6px 12px', fontWeight: 700}}>
              Création d&apos;une nouvelle image du dossier
            </div>
            <div style={{padding: 16}}>
              <p style={{margin: '0 0 12px 0'}}>
                Les changements suivants nécessitent la création d&apos;une <strong>nouvelle image</strong> :
              </p>
              <ul style={{margin: '0 0 12px 18px', padding: 0, color: '#8b1d1d', fontWeight: 600}}>
                {revisionModal.changements.map((c, i) => (<li key={i}>Changement {c}</li>))}
              </ul>
              <div style={{display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', marginBottom: 12, padding: 8, background: '#fff8d0', borderLeft: '3px solid #c8a000'}}>
                <strong>Image actuelle :</strong> <span data-testid="revision-image-actuelle">{revisionModal.image_actuelle}</span>
                <strong>Nouvelle image :</strong> <span data-testid="revision-image-nouvelle">{revisionModal.image_nouvelle}</span>
              </div>
              <label style={{display: 'block', fontWeight: 600, marginBottom: 4}}>
                Note de révision <span style={{color: '#c00'}}>*</span> :
              </label>
              <textarea
                className="win-input"
                value={revisionModal.note}
                onChange={e => setRevisionModal(prev => ({ ...prev, note: e.target.value }))}
                rows={4}
                placeholder="Expliquez la raison du changement (obligatoire)"
                style={{width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 12}}
                data-testid="revision-note"
                autoFocus
              />
            </div>
            <div style={{padding: 8, display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#e0e0e0'}}>
              <button
                type="button"
                className="win-btn"
                onClick={() => setRevisionModal({ open: false, changements: [], image_actuelle: '', image_nouvelle: '', note: '', pendingPayload: null })}
                data-testid="revision-cancel"
              >
                Annuler
              </button>
              <button
                type="button"
                className="win-btn"
                onClick={confirmerNouvelleImage}
                disabled={!revisionModal.note?.trim()}
                style={{fontWeight: 600}}
                data-testid="revision-confirm"
              >
                Créer la nouvelle image
              </button>
            </div>
          </div>
        </div>
      )}
      {pdfPreview.open && (
        <PDFPreviewModal
          previewUrl={pdfPreview.previewUrl}
          downloadUrl={pdfPreview.downloadUrl}
          seq={pdfPreview.seq}
          onClose={() => setPdfPreview({ open: false, previewUrl: null, downloadUrl: null, seq: null })}
        />
      )}
      {dajPrint.open && (
        <DajPrintModal
          seq={dajPrint.seq}
          conjointAvailable={dajPrint.conjointAvailable}
          onClose={() => setDajPrint({ open: false, seq: null, conjointAvailable: false })}
        />
      )}
      {confirmDirty.isOpen && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000}} data-testid="confirm-dirty-overlay">
          <div style={{backgroundColor: '#f0f0f0', border: '2px solid #333', width: '420px'}} data-testid="confirm-dirty-modal">
            <div style={{backgroundColor: '#003366', color: 'white', padding: '6px 14px', fontWeight: 'bold', fontSize: '13px'}}>ATTENTION</div>
            <div style={{padding: '20px 24px'}}>
              <p style={{margin: '0 0 20px 0', fontSize: '13px'}}>Des modifications sont en cours, voulez-vous</p>
              <div style={{display: 'flex', justifyContent: 'center', gap: 16}}>
                <button className="win-btn" onClick={handleDirtySave} style={{padding: '6px 24px', fontWeight: 'bold', fontSize: '12px'}} data-testid="confirm-dirty-save">SAUVEGARDER</button>
                <button className="win-btn" onClick={handleDirtyDiscard} style={{padding: '6px 24px', fontSize: '12px'}} data-testid="confirm-dirty-discard">ANNULER</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {confirmBureauSwitch && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000}} data-testid="confirm-bureau-overlay">
          <div style={{backgroundColor: '#f0f0f0', border: '2px solid #333', width: '460px'}} data-testid="confirm-bureau-modal">
            <div style={{backgroundColor: '#003366', color: 'white', padding: '6px 14px', fontWeight: 'bold', fontSize: '13px'}}>CHANGER DE BUREAU</div>
            <div style={{padding: '20px 24px'}}>
              <p style={{margin: '0 0 20px 0', fontSize: '13px'}}>
                Un dossier est actuellement ouvert. Pour changer de bureau, il doit être
                sauvegardé et fermé. Voulez-vous continuer ?
              </p>
              <div style={{display: 'flex', justifyContent: 'center', gap: 16}}>
                <button className="win-btn" onClick={handleBureauSwitchSaveClose} style={{padding: '6px 24px', fontWeight: 'bold', fontSize: '12px'}} data-testid="confirm-bureau-save">SAUVEGARDER ET FERMER</button>
                <button className="win-btn" onClick={() => setConfirmBureauSwitch(false)} style={{padding: '6px 24px', fontSize: '12px'}} data-testid="confirm-bureau-annuler">ANNULER</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {confirmMineur.isOpen && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000}} data-testid="confirm-mineur-overlay">
          <div style={{backgroundColor: '#f0f0f0', border: '2px solid #333', width: '460px'}} data-testid="confirm-mineur-modal">
            <div style={{backgroundColor: '#d97706', color: 'white', padding: '6px 14px', fontWeight: 'bold', fontSize: '13px'}}>Informations financières</div>
            <div style={{padding: '20px 24px'}}>
              <p style={{margin: '0 0 12px 0', fontSize: '13px', whiteSpace: 'pre-line'}}>
                {"Le requérant est un mineur, aucune information financière n'est requise\nL'onglet revenus sera désactivé."}
              </p>
              {confirmMineur.hasFinancial && (
                <p style={{margin: '0 0 12px 0', fontSize: '13px', fontWeight: 'bold', color: '#92400e'}} data-testid="confirm-mineur-erase-warning">
                  Des informations financières (revenus et/ou actifs) ont déjà été saisies. En continuant, ces informations seront effacées.
                </p>
              )}
              <p style={{margin: '0 0 20px 0', fontSize: '13px'}}>Voulez-vous continuer ?</p>
              <div style={{display: 'flex', justifyContent: 'center', gap: 16}}>
                <button className="win-btn" onClick={handleMineurConfirmOui} style={{padding: '6px 24px', fontWeight: 'bold', fontSize: '12px'}} data-testid="confirm-mineur-oui">OUI</button>
                <button className="win-btn" onClick={handleMineurConfirmNon} style={{padding: '6px 24px', fontSize: '12px'}} data-testid="confirm-mineur-non">NON</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {reciprociteChoice && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000}} data-testid="reciprocite-lang-overlay" onClick={() => setReciprociteChoice(null)}>
          <div style={{backgroundColor: '#f0f0f0', border: '2px solid #333', width: '400px'}} data-testid="reciprocite-lang-modal" onClick={e => e.stopPropagation()}>
            <div style={{backgroundColor: '#003366', color: 'white', padding: '6px 14px', fontWeight: 'bold', fontSize: '13px'}}>{reciprociteChoice.title} — choisir la langue</div>
            <div style={{padding: '20px 24px'}}>
              <p style={{margin: '0 0 20px 0', fontSize: '13px'}}>Dans quelle langue souhaitez-vous imprimer ce formulaire ?</p>
              <div style={{display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap'}}>
                {(reciprociteChoice.langs || ['fr']).map(lg => {
                  const label = lg === 'fr' ? 'Français' : lg === 'en' ? 'Anglais' : 'Espagnol';
                  return (
                    <button key={lg} className="win-btn" style={{padding: '6px 20px', fontWeight: 'bold', fontSize: '12px'}} data-testid={`reciprocite-lang-${lg}`}
                            onClick={() => {
                              const params = [`langue=${lg}`];
                              if (authUser?.region) params.push(`region=${encodeURIComponent(authUser.region)}`);
                              window.open(`${API}/rapports/${reciprociteChoice.id}/pdf?${params.join('&')}`, '_blank');
                              setReciprociteChoice(null);
                            }}>{label}</button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      {showJEConfig && <JurisEvolutionConfigModal onClose={() => setShowJEConfig(false)} showPopup={showPopup} />}

      {jeSyncing && (
        <div data-testid="juris-sync-overlay"
             style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000001 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '26px 30px', minWidth: 320,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.25)', textAlign: 'center',
                        borderTop: '4px solid #d97706' }}>
            <Loader2 size={40} style={{ color: '#d97706', animation: 'je-spin 0.9s linear infinite' }} />
            <div data-testid="juris-sync-overlay-title" style={{ marginTop: 14, fontWeight: 700, fontSize: 16, color: '#111827' }}>
              {jeSyncPhase === 'sync' ? 'Synchronisation en cours…' : 'Validation des données en cours…'}
            </div>
            <div style={{ marginTop: 6, fontSize: 13, color: '#6b7280' }}>
              {jeSyncPhase === 'sync'
                ? 'Envoi du dossier vers JurisÉvolution. Veuillez patienter.'
                : 'Vérification des données avant la synchronisation vers JurisÉvolution.'}
            </div>
          </div>
        </div>
      )}
      {showCalibration && <PDFCalibrationTool onClose={() => setShowCalibration(false)} />}
      {showAttestCalibration && <AttestationPDFCalibrationTool onClose={() => setShowAttestCalibration(false)} />}
      {showGestionRapports && (
        <GestionRapportsModal
          onClose={() => setShowGestionRapports(false)}
          currentNoseqdaj={formData.no_sequence || currentDossier?.no_seq_daj || ''}
          userRegion={authUser?.region || ''} />
      )}
      <InteretsOpposesResultsModal
        data={verifIntOppResults}
        onClose={() => setVerifIntOppResults(null)}
      />
    </div>
  );
}

export default App;
